// Extended per-beach content: coords + tide station for live conditions,
// getting-there / safety / FAQ / activities / packing / photo / seasonal modules.
// Attached to seeded profiles at module load time (see beachProfiles.ts).

import type {
  BeachTidePool,
  BeachGettingThere,
  BeachSafety,
  BeachFaq,
  BeachActivity,
  BeachPacking,
  BeachPhotoGuide,
  BeachSeasonalCalendar,
  BeachPhoto,
  BeachDiningPick,
  BeachNearbyAttraction,
  BeachBestForItem,
  BeachParkingSnapshot,
} from "./beachProfiles";

export interface BeachExtras {
  coords?: { lat: number; lng: number };
  tideStationId?: string;
  gettingThere?: BeachGettingThere;
  safety?: BeachSafety;
  faqs?: BeachFaq[];
  activities?: BeachActivity[];
  packing?: BeachPacking;
  photoGuide?: BeachPhotoGuide;
  seasonalCalendar?: BeachSeasonalCalendar;
  photoGallery?: BeachPhoto[];
  dining?: BeachDiningPick[];
  nearbyAttractions?: BeachNearbyAttraction[];
  bestFor?: BeachBestForItem[];
  parkingSnapshot?: BeachParkingSnapshot;
  tidePool?: BeachTidePool;
}

const LA_JOLLA = "9410230";
const SAN_DIEGO_BAY = "9410170";
// Oceanside has no NOAA tide-prediction station; La Jolla is NOAA's
// official reference for the North County coast.
const NORTH_COUNTY = LA_JOLLA;
const SOUTH_BAY = "9410135";
const MISSION_BAY = "9410196";
const IMPERIAL_BEACH = "9410120";

// Reusable water-temp row (Jan–Dec) — SD is 0=cold, 3=warm
const WATER_TEMP_SD = [0, 0, 0, 1, 1, 2, 2, 3, 3, 2, 1, 0];
// Weather sun row — SD typical
const SUN_SD = [1, 1, 1, 2, 1, 1, 3, 3, 3, 2, 2, 1]; // May/June gloom = 1
// North County has more May Gray / June Gloom
const SUN_NORTH = [1, 1, 1, 2, 1, 0, 2, 3, 3, 2, 2, 1];

export const BEACH_EXTRAS: Record<string, BeachExtras> = {
  "la-jolla-cove": {
    coords: { lat: 32.8503, lng: -117.2717 },
    tideStationId: LA_JOLLA,
    gettingThere: {
      driving:
        "From I-5, exit La Jolla Village Drive west, follow Torrey Pines Rd → Prospect St into La Jolla village. Cove is at the north end of Coast Blvd.",
      parking:
        "Free lot at Ellen Browning Scripps Park (fills by 9am weekends). Metered street parking on Coast Blvd and Prospect ($2.50/hr, until 6pm). Paid lots on Herschel and Silverado run $15–25 for the day.",
      transit:
        "MTS Route 30 stops at Prospect & Girard, 5-min walk to the cove. Bike racks at the park entrance.",
      tips: [
        "Arrive before 8am on summer weekends or plan for a 10-minute walk from lower Girard St.",
        "The lot at Coast & Cave St is the closest but turns over fastest — circle back after 3pm.",
      ],
    },
    safety: {
      rip: "Low — the cove is naturally sheltered. Rip risk rises just outside the point during south swells.",
      hazards: [
        "Slippery rocks and barnacles at low tide entry",
        "Sea lions are wild animals — keep 50 ft distance, especially near pups",
        "Kelp tangles common during summer — swim wide of the beds",
      ],
      lifeguard:
        "Seasonal tower staffed daily May–Sept, weekends only shoulder season. Emergency line: 619-221-8899.",
      waterQuality:
        "Generally excellent (Grade A on sdbeachinfo.com). Bacteria can spike 72 hours after heavy rain — check before swimming.",
    },
    faqs: [
      { q: "Can I swim at La Jolla Cove?", a: "Yes. It's one of the calmest, clearest swimming spots in San Diego, protected by the surrounding cliffs. The best swimming happens at a tide of 1.5 ft or lower when the sandy beach is exposed." },
      { q: "Are the sea lions dangerous?", a: "They're wild and can be territorial, especially bulls during breeding season and mothers with pups. Keep at least 50 feet away, never block their access to the water, and don't try to swim through a group." },
      { q: "Where can I park for free near La Jolla Cove?", a: "The Ellen Browning Scripps Park lot on Coast Blvd is free but fills by 9am on weekends. Free residential parking exists 5–10 minutes uphill on Draper, Cave, or Coast Walk. All village streets are metered until 6pm." },
      { q: "When can I see leopard sharks at La Jolla Cove?", a: "Juvenile leopard sharks gather in the shallows off La Jolla Shores (10 minutes north) from mid-June through October, peaking in August and September. They're harmless and prefer knee-to-waist deep water." },
      { q: "Are dogs allowed at La Jolla Cove?", a: "No. The cove is inside the San Diego–La Jolla Underwater Park, a marine reserve where dogs are prohibited on the beach and in the water. Leashed dogs are allowed on the grass at Scripps Park above." },
    ],
    activities: [
      { kind: "snorkel", rating: "great", note: "Marine reserve, garibaldi" },
      { kind: "swim", rating: "great", note: "Calm cove" },
      { kind: "photo", rating: "great" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good", note: "Grass at Scripps Park" },
      { kind: "kayak", rating: "good", note: "Launch from LJ Shores" },
      { kind: "surf", rating: "avoid" },
      { kind: "dogs", rating: "avoid", note: "Prohibited in reserve" },
      { kind: "bonfire", rating: "avoid" },
    ],
    packing: {
      bring: [
        "Mask, snorkel, fins",
        "Reef-safe SPF (mineral only)",
        "Water shoes for the rocky entry",
        "3mm wetsuit Nov–May",
        "Small towel — sand disappears at high tide",
      ],
      rentNearby: [
        "Snorkel gear at Everyday California (Avenida de la Playa)",
        "Wetsuits at OEX Dive & Kayak",
        "Kayak tours from La Jolla Shores (10 min north)",
      ],
      skip: [
        "Dogs (marine reserve)",
        "Chairs / umbrellas — no room at high tide",
        "Feeding sea lions — illegal, $10k fine",
      ],
    },
    photoGuide: {
      bestTime:
        "45 minutes before sunset year-round; June–August sunsets align straight into the cove mouth.",
      bestSpots: [
        "Scripps Park bluff above the cove (postcard angle)",
        "Sea lion overlook at Point La Jolla",
        "Water-level shot from the north stairway at low tide",
      ],
      tips: [
        "Shoot from the north bluff so the sun stays over your shoulder.",
        "Golden light hits the sandstone cliffs 30 min before sunset — face east then.",
        "Low tide + calm sea = mirror reflections in the tidepools north of the cove.",
      ],
    },
    seasonalCalendar: {
      summary:
        "Best months are September–October: warmest water, calm swell, minimal crowds after Labor Day.",
      rows: {
        crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 1, 1, 1],
        water: WATER_TEMP_SD,
        wildlife: [2, 2, 1, 1, 2, 3, 3, 3, 3, 2, 2, 2],
        weather: SUN_SD,
      },
      events: [
        { months: "Aug–Oct", label: "Leopard sharks at LJ Shores (10 min north)" },
        { months: "Dec–Apr", label: "Gray whale migration visible from the bluff" },
        { months: "Jun–Sep", label: "Peak snorkeling, warmest & clearest water" },
      ],
    },
    bestFor: [
      { label: "Snorkeling with garibaldi and leopard sharks", icon: "Fish" },
      { label: "Calm, clear swimming inside the marine reserve", icon: "Waves" },
      { label: "Sea lion and marine life watching", icon: "Camera" },
    ],
    parkingSnapshot: {
      cost: "Free at Ellen Browning Scripps Park; metered street $2.50/hr",
      bestLot: "Free lot at Coast Blvd & Prospect St (fills by 9am weekends)",
      backupLot: "Residential parking on Cave St / Coast Walk (5–10 min walk)",
      fillsBy: "9am weekends; 10am weekdays in summer",
      notes: "Paid lots on Herschel and Silverado run $15–25/day when street parking is full.",
    },
    dining: [
      { slug: "george-s-at-the-cove", reason: "Rooftop ocean views above the cove — ideal post-snorkel dinner", distance: "3-min walk" },
      { slug: "the-taco-stand", reason: "Al pastor tacos carved to order — the value pick in the village", distance: "6-min walk" },
      { slug: "cody-s-la-jolla", reason: "Cottage-patio breakfast a block above the cove", distance: "2-min walk" },
      { slug: "puesto-la-jolla", reason: "Crispy-cheese tacos and mezcal cocktails, upscale-casual", distance: "8-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/db46701e-d6e9-4f6f-a401-cf3bf529b1de/la-jolla-cove-0.jpg", alt: "Bright orange garibaldi fish swimming through kelp at La Jolla Cove", caption: "Garibaldi — California's state marine fish — are easy to spot just offshore." },
      { src: "/__l5e/assets-v1/5df72694-c7f4-45bd-8634-fa4d82345193/la-jolla-cove-1.jpg", alt: "Wide sandy beach at La Jolla Cove framed by sandstone cliffs", caption: "Low tide exposes the full crescent of sand between the bluffs." },
      { src: "/__l5e/assets-v1/6993d402-a382-4f4e-9fd6-ad3f42ef9b90/la-jolla-cove-2.jpg", alt: "Sea lion resting on rocks near La Jolla Cove", caption: "Sea lions haul out on the rocks just north of the cove." },
    ],
    nearbyAttractions: [
      { to: "/guides/la-jolla-cove-sea-lions", label: "La Jolla Cove Sea Lions Guide", hint: "How to watch responsibly and where to find them" },
      { to: "/guides/la-jolla-cove-snorkeling", label: "Snorkeling La Jolla Cove", hint: "Gear, entry points, and what you'll see underwater" },
      { to: "/guides/la-jolla-tide-pools", label: "La Jolla Tide Pools", hint: "Best low-tide routes around the cove" },
      { to: "/beaches/la-jolla-shores", label: "La Jolla Shores", hint: "A mile of family-friendly sand 5 minutes north" },
    ],
  },

  "la-jolla-shores": {
    coords: { lat: 32.8578, lng: -117.2564 },
    tideStationId: LA_JOLLA,
    gettingThere: {
      driving:
        "From I-5, exit La Jolla Village Drive west, turn left on Torrey Pines Rd, then right on La Jolla Shores Dr, then right on Avenida de la Playa which dead-ends at Kellogg Park.",
      parking:
        "Free lot at Kellogg Park (fills by 9am summer). Free 2-hr street parking on La Jolla Shores Dr and Camino del Oro. No meters in the immediate beach neighborhood.",
      transit: "MTS Route 30 stops at La Jolla Shores Dr & Avenida de la Playa.",
      tips: [
        "Arrive before 8am for a Kellogg Park spot, or come after 4pm when day-trippers leave.",
        "Camino del Oro (one block back from the beach) usually has open spots when the lot is full.",
      ],
    },
    safety: {
      rip: "Low to moderate. Rip currents strengthen during larger south swells — swim between the flags.",
      hazards: [
        "Stingrays in the shallows summer through fall — shuffle your feet",
        "Learner surfers everywhere in the morning — watch for boards",
      ],
      lifeguard:
        "Year-round central tower plus seasonal secondary towers. Junior Guards program runs the beach June–August.",
      waterQuality:
        "Generally excellent. Occasional advisories after heavy rain — check sdbeachinfo.com.",
    },
    faqs: [
      { q: "Is La Jolla Shores good for kids?", a: "It's the most family-friendly beach in San Diego. The bottom slopes gently, waves rarely top 3 ft, lifeguards staff a central tower year-round, and Kellogg Park behind the sand has a playground, restrooms, showers, and picnic tables." },
      { q: "Where should I take a surf lesson in San Diego?", a: "La Jolla Shores is the top spot. Surf Diva, San Diego Surfing School, and half a dozen others run morning lessons here. The sandy bottom, mellow waves, and patient lineup make it the most forgiving classroom in the city." },
      { q: "How do I avoid stingrays at La Jolla Shores?", a: "Shuffle your feet along the sand as you walk in — the vibration scares them off. Stings peak from July through October in the shallows near shore." },
      { q: "Are there fire pits at La Jolla Shores?", a: "Yes — concrete fire rings sit on the south end of the beach near the lifeguard tower. They're first-come, first-served, and fill by mid-afternoon on summer weekends." },
      { q: "Can I kayak to the sea caves from La Jolla Shores?", a: "Yes. Bike & Kayak Tours, La Jolla Kayak, and Everyday California all launch guided trips from the south end of the beach. The paddle to the seven La Jolla Sea Caves is about 30 minutes each way." },
    ],
    activities: [
      { kind: "swim", rating: "great", note: "Gentle, sandy bottom" },
      { kind: "surf", rating: "great", note: "Top learn-to-surf spot" },
      { kind: "kayak", rating: "great", note: "Launch to sea caves" },
      { kind: "sup", rating: "great" },
      { kind: "bonfire", rating: "good", note: "South-end fire rings" },
      { kind: "picnic", rating: "great", note: "Kellogg Park" },
      { kind: "dogs", rating: "avoid", note: "Not allowed on sand" },
      { kind: "snorkel", rating: "fair", note: "Better at the Cove" },
      { kind: "sunset", rating: "good" },
    ],
    packing: {
      bring: [
        "Sunscreen and a wide-brim hat",
        "Beach chairs & umbrella (plenty of room)",
        "Cooler for a picnic lunch",
        "Firewood if you're claiming a fire ring",
        "Shuffle-safe water shoes summer through fall",
      ],
      rentNearby: [
        "Surfboards & wetsuits at Surf Diva or Emerald City",
        "Kayak & SUP rentals at Everyday California",
        "Beach cruisers on Avenida de la Playa",
      ],
      skip: [
        "Glass containers (banned)",
        "Alcohol (banned on all SD city beaches)",
        "Off-leash dogs — nearest dog beach is Dog Beach in OB",
      ],
    },
    photoGuide: {
      bestTime:
        "First light for empty-beach shots; last hour before sunset for warm bluff light.",
      bestSpots: [
        "South end near the lifeguard tower (waves + Scripps Pier)",
        "Scripps Pier at low tide",
        "Kellogg Park lawn for family shots",
      ],
      tips: [
        "Winter king tides push waves up against the pier for dramatic frames.",
        "Point your camera north at sunset — the bluffs catch the last light.",
      ],
    },
    seasonalCalendar: {
      summary:
        "The gentlest year-round beach in SD. Peak family season is July–August; September has the warmest water with lighter crowds.",
      rows: {
        crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1],
        water: WATER_TEMP_SD,
        surf: [1, 1, 1, 1, 2, 2, 2, 2, 2, 1, 1, 1],
        wildlife: [1, 1, 1, 1, 2, 3, 3, 3, 3, 2, 1, 1],
        weather: SUN_SD,
      },
      events: [
        { months: "Jun–Aug", label: "Junior Guards on the beach 8am–12pm" },
        { months: "Aug–Oct", label: "Leopard shark aggregation in the shallows" },
        { months: "Jul–Oct", label: "Stingray season — shuffle your feet" },
      ],
    },
    bestFor: [
      { label: "Families with young kids", icon: "Users" },
      { label: "First-time surfers and surf lessons", icon: "Waves" },
      { label: "Kayaking to the La Jolla Sea Caves", icon: "Fish" },
    ],
    parkingSnapshot: {
      cost: "Free in Kellogg Park lot; street parking is free but competitive",
      bestLot: "Kellogg Park lot at Avenida de la Playa (fills by 9am weekends)",
      backupLot: "Residential streets off Avenida de la Playa (10-min walk)",
      fillsBy: "9am weekends; 10am weekdays in July–August",
      notes: "Arrive before 8am for the closest spots; after 4pm the lot turns over quickly.",
    },
    dining: [
      { slug: "the-marine-room", reason: "Waves-break-against-the-windows fine dining at the south end", distance: "5-min walk" },
      { slug: "puesto-la-jolla", reason: "Street-food tacos and cocktails 5 minutes south", distance: "6-min drive" },
      { slug: "the-taco-stand", reason: "Cheap, excellent tacos when the Shores lots empty out", distance: "7-min drive" },
      { slug: "george-s-at-the-cove", reason: "Rooftop sunset spot down in the village", distance: "8-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/5099ed9d-c99f-4daf-bd6b-f7d065434882/la-jolla-shores-0.jpg", alt: "Long, flat sandy beach at La Jolla Shores with gentle waves", caption: "The Shores' mile-long sandbar makes it the gentlest entry in San Diego." },
      { src: "/__l5e/assets-v1/25499dfc-bfb6-439b-89fe-962c3a40154b/la-jolla-shores-1.jpg", alt: "Kayakers paddling toward the La Jolla Sea Caves", caption: "Kayak tours launch from the south end toward the marine reserve." },
      { src: "/__l5e/assets-v1/2961e282-6ce1-4945-a143-f670b8eef057/la-jolla-shores-2.jpg", alt: "Sunset over the Scripps Pier at La Jolla Shores", caption: "The Scripps Pier is one of the most photographed sunset spots in California." },
    ],
    nearbyAttractions: [
      { to: "/guides/la-jolla-cove-snorkeling", label: "Snorkeling & Kayaking Guide", hint: "From the Shores to the sea caves and cove" },
      { to: "/guides/best-san-diego-tide-pools", label: "Best San Diego Tide Pools", hint: "Low-tide exploration routes nearby" },
      { to: "/beaches/la-jolla-cove", label: "La Jolla Cove", hint: "Calm snorkeling cove 5 minutes south" },
      { to: "/guides/san-diego-with-kids", label: "San Diego With Kids", hint: "Family itineraries that start at the Shores" },
    ],
  },

  "coronado-beach": {
    coords: { lat: 32.6803, lng: -117.1831 },
    tideStationId: SAN_DIEGO_BAY,
    gettingThere: {
      driving:
        "Cross the Coronado Bridge (CA-75), turn left on Orange Ave, follow it to the Hotel Del. Central Beach access is at Ocean Blvd & Loma Ave.",
      parking:
        "Free street parking east of Orange Ave — walk 2–4 blocks. Ocean Blvd curbside fills fast near the Hotel Del. Paid hotel valet only on-site.",
      transit:
        "Coronado Ferry from downtown San Diego ($7 each way, 15 min). MTS Route 904 loops the island. Bayshore Bikeway connects from Imperial Beach.",
      tips: [
        "Take the ferry for a car-free arrival, then rent a bike at Ferry Landing.",
        "Avenida del Sol and G Ave usually have open spots when Ocean Blvd is full.",
      ],
    },
    safety: {
      rip: "Low to moderate. Waves are gentle by San Diego standards; rip risk rises with WNW winter swells.",
      hazards: [
        "Sudden shore-break drop-offs at higher tides — watch small kids",
        "Naval helicopter and jet noise from North Island — normal, not an emergency",
      ],
      lifeguard: "Year-round central tower plus seasonal towers spaced along the beach.",
      waterQuality:
        "Excellent almost year-round. Beach wheelchairs and rinse showers at the Central Beach lifeguard station.",
    },
    faqs: [
      { q: "Why does the sand at Coronado Beach sparkle?", a: "The sparkle is trace amounts of the mineral mica (biotite) mixed into the sand. It shimmers most visibly in direct sunlight and around sunset — one of the reasons Coronado has been ranked America's #1 beach multiple times." },
      { q: "Are dogs allowed on Coronado Beach?", a: "Yes at the north end (Coronado Dog Beach), which is off-leash 24/7 west of Sunset Park. The main Central Beach requires leashes. Dogs are not allowed on the sand in front of the Hotel Del." },
      { q: "Can you have a bonfire on Coronado Beach?", a: "Yes — cement fire pits sit at the south end of Central Beach near the Hotel Del. They're first-come, first-served. Bring your own wood; no pallets or trash burning." },
      { q: "How do I get to Coronado without driving?", a: "The Coronado Ferry runs from Broadway Pier and the Convention Center downtown to Ferry Landing on Coronado ($7 one-way, ~15 minutes). MTS Route 901 also connects downtown to Coronado." },
      { q: "Is Coronado Beach free?", a: "Yes. There's no entry fee, no parking meter at the beach lots, and public restrooms, showers, and fire pits are all free." },
    ],
    activities: [
      { kind: "swim", rating: "great", note: "Wide, gentle shore-break" },
      { kind: "sunset", rating: "great", note: "Hotel Del glows red" },
      { kind: "photo", rating: "great" },
      { kind: "bonfire", rating: "great", note: "South-end pits" },
      { kind: "dogs", rating: "good", note: "Off-leash at north end" },
      { kind: "bike", rating: "great", note: "Bayshore Bikeway" },
      { kind: "picnic", rating: "great" },
      { kind: "surf", rating: "fair", note: "Better on WNW winter swells" },
      { kind: "snorkel", rating: "avoid", note: "Sandy, not much to see" },
    ],
    packing: {
      bring: [
        "Sunscreen — no shade for two miles",
        "Beach chairs & umbrella (endless room)",
        "Firewood for the south-end pits",
        "Cash for the ferry ($7 each way)",
        "Cooler — no glass",
      ],
      rentNearby: [
        "Beach cruisers at Coronado Ferry Landing",
        "Surreys and umbrellas at Wheel Fun Rentals",
        "Boogie boards at the Hotel Del beach shack",
      ],
      skip: [
        "Glass containers & alcohol",
        "Drones — restricted airspace near NAS North Island",
        "Off-leash dogs at Central Beach (north end only)",
      ],
    },
    photoGuide: {
      bestTime:
        "15 minutes before sunset — the Hotel Del's red turrets glow against the sky for about a quarter hour.",
      bestSpots: [
        "Sand in front of the Hotel Del looking north-east",
        "Walk south past the Del for the wide-beach + turret frame",
        "Rock jetty at the north end for the mica-sparkle close-up",
      ],
      tips: [
        "Wet sand at low tide mirrors the Del at sunset.",
        "Bring a polarizer — the mica sparkle pops on camera when angled to the sun.",
      ],
    },
    seasonalCalendar: {
      summary:
        "Consistent beach year-round. September is the sweet spot: warmest water, lightest crowds, best sunsets.",
      rows: {
        crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1],
        water: WATER_TEMP_SD,
        surf: [2, 2, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2],
        wildlife: [2, 2, 2, 2, 1, 1, 1, 1, 1, 2, 2, 2],
        weather: SUN_SD,
      },
      events: [
        { months: "Jul 4", label: "Big Bay Boom fireworks visible from the beach" },
        { months: "Dec–Apr", label: "Gray whale migration offshore" },
        { months: "Aug", label: "Coronado Speed Festival + peak beach season" },
      ],
    },
    bestFor: [
      { label: "Wide, uncrowded walks and sunsets", icon: "Sun" },
      { label: "Families and gentle wading", icon: "Users" },
      { label: "Beachfront bonfires near the Hotel Del", icon: "Flame" },
    ],
    parkingSnapshot: {
      cost: "Free street parking east of Orange Ave; Hotel Del valet is paid",
      bestLot: "Free curbside on Ocean Blvd & Loma Ave (fills by 10am)",
      backupLot: "Residential streets 2–4 blocks east of Orange Ave",
      fillsBy: "10am weekends; earlier on holidays and summer weekdays",
      notes: "The ferry from downtown drops you a 15-minute walk from the sand.",
    },
    dining: [
      { slug: "1500-ocean", reason: "Oceanfront fine dining inside the iconic Hotel del Coronado", distance: "2-min walk" },
      { slug: "clayton-s-coffee-shop", reason: "Chrome-stool diner breakfast at pre-resort prices", distance: "10-min walk" },
      { slug: "coronado-brewing-company", reason: "House IPAs, fish tacos and burgers on a shaded patio", distance: "12-min walk" },
      { slug: "stake-chophouse-bar", reason: "Upscale chophouse and craft cocktails on Orange Ave", distance: "12-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/ef211efa-9e47-4313-ab1c-a6b4d1765228/coronado-beach-0.jpg", alt: "Hotel del Coronado red turrets glowing at sunset with wide beach", caption: "The Hotel Del's red turrets are the classic Coronado sunset frame." },
      { src: "/__l5e/assets-v1/741f1c26-3d97-4673-b08d-bc2bbdea95a7/coronado-beach-1.jpg", alt: "Sparkling wide sand at Coronado Beach", caption: "Coronado's mica-rich sand sparkles in the afternoon light." },
      { src: "/__l5e/assets-v1/357c79d2-7f3d-4bfe-a879-b198e5c3db58/coronado-beach-2.jpg", alt: "Dog Beach at the north end of Coronado", caption: "The north end is an off-leash dog beach with plenty of room to run." },
    ],
    nearbyAttractions: [
      { to: "/guides/best-beaches-in-san-diego", label: "Best Beaches in San Diego", hint: "How Coronado ranks against the rest of the coast" },
      { to: "/guides/san-diego-with-kids", label: "San Diego With Kids", hint: "Family itineraries built around Coronado's calm water" },
      { to: "/beaches/silver-strand", label: "Silver Strand State Beach", hint: "A quieter, car-camping beach on the isthmus" },
      { to: "/dining", label: "San Diego Dining Guide", hint: "More Coronado and downtown restaurants" },
    ],
  },

  "torrey-pines": {
    coords: { lat: 32.9203, lng: -117.2611 },
    tideStationId: LA_JOLLA,
    gettingThere: {
      driving:
        "From I-5, exit Carmel Valley Rd west, then left on N Torrey Pines Rd. The state beach lot is at the bottom of the hill, before the reserve.",
      parking:
        "State beach lot: $12–25 daily (rate varies weekends/holidays), fills by 10am summer. Free residential parking on the bluff above off Del Mar Scenic Pkwy — long stairway down.",
      transit:
        "MTS Route 101 stops along N Torrey Pines Rd. No bike lane on the coast highway — take the inland path.",
      tips: [
        "Buy the annual state parks pass ($195) if you plan to visit more than 8 times a year.",
        "The North and South beach lots remain open; the reserve road, upper lots, and Beach Trail Staircase are closed through February 2027.",
      ],
    },
    safety: {
      rip: "Moderate. The beachbreak is exposed to open ocean — rip currents strengthen quickly on head-high days.",
      hazards: [
        "Cliff falls — never sit or walk directly against the sandstone bluffs, they collapse without warning",
        "Rising tides can trap hikers on the beach — check the tide chart before walking north to Blacks",
        "No shade for miles — bring sun protection",
      ],
      lifeguard:
        "Seasonal tower at Torrey Pines State Beach entrance, staffed daily summer, weekends shoulder season.",
      waterQuality:
        "Excellent — one of the cleanest beaches in the county. Occasional advisories after major rain events.",
    },
    faqs: [
      { q: "Are dogs allowed at Torrey Pines State Beach?", a: "No. Dogs are prohibited on Torrey Pines State Beach and inside the Torrey Pines State Natural Reserve, even on leash. Del Mar Dog Beach starts about a mile north." },
      { q: "Can I walk from Torrey Pines to Blacks Beach?", a: "Yes — it's about 2 miles north along the sand. Only attempt it at a tide of 3 ft or lower and be back before the tide comes back up, or you can get pinned against the cliffs." },
      { q: "How much does it cost to park at Torrey Pines?", a: "The open North and South beach lots use demand-based pricing, generally $10–25. Both upper reserve lots and the park road are closed through February 2027. Confirm current rates with California State Parks before visiting." },
      { q: "Are the Torrey Pines trails open?", a: "No. The State Natural Reserve, all trails, Beach Trail Staircase, Lodge, park road, and upper parking lots are closed from September 22, 2026 through February 2027. Full access is expected March 1, 2027. The state beach and North and South beach lots remain open." },
      { q: "Is Torrey Pines State Beach safe to swim?", a: "Yes, but treat it as an ocean beach — rip currents form quickly, there's no protected cove, and lifeguards are seasonal. Stay between the flags when they're up." },
    ],
    activities: [
      { kind: "swim", rating: "good", note: "Between the flags" },
      { kind: "surf", rating: "good", note: "Exposed beachbreak" },
      { kind: "photo", rating: "great", note: "Sandstone bluffs" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good", note: "Grassy area at reserve entrance" },
      { kind: "fish", rating: "good", note: "Surf fishing from the sand" },
      { kind: "tidepool", rating: "fair", note: "Small pools at north end low tide" },
      { kind: "dogs", rating: "avoid", note: "Prohibited on state beach" },
      { kind: "bonfire", rating: "avoid", note: "Not allowed at state beach" },
    ],
    packing: {
      bring: [
        "Wide-brim hat & high-SPF — zero shade",
        "1 gallon water per person",
        "Tide chart printed or downloaded",
        "Walking shoes for the open beach; reserve trails are closed",
        "State parks day-use pass or $195 annual",
      ],
      rentNearby: [
        "Nothing on-site — grab gear in Del Mar (1 mi north)",
        "Surfboards at Cardiff Surf Co (5 mi north)",
      ],
      skip: [
        "Dogs — prohibited beach & reserve",
        "Umbrella stakes into the bluff (protected sandstone)",
        "Anything glass",
      ],
    },
    photoGuide: {
      bestTime:
        "One hour before sunset in winter, when the sun sets over the ocean and the bluffs turn gold.",
      bestSpots: [
        "Flat Rock at the base of the Beach Trail (low tide only)",
        "The wide sand north of the South Beach lot",
        "Los Peñasquitos Lagoon edge near the North Beach lot",
      ],
      tips: [
        "Approach at a -0.5 to +2 ft tide for the widest sand.",
        "Shoot from the water's edge looking east so the bluffs fill the frame.",
        "Never scramble the bluffs — they collapse without warning and the fine is steep.",
      ],
    },
    seasonalCalendar: {
      summary:
        "Best in autumn: warm water, mild south swells, and the reserve is glowing gold. Winter king tides trim the beach dramatically.",
      rows: {
        crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1],
        water: WATER_TEMP_SD,
        surf: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3],
        wildlife: [3, 3, 2, 1, 1, 1, 1, 1, 2, 2, 2, 3],
        weather: SUN_SD,
      },
      events: [
        { months: "Dec–Apr", label: "Gray whales visible from the bluffs" },
        { months: "Feb–Mar", label: "Wildflowers in the reserve" },
        { months: "Nov–Jan", label: "King tides — dramatic wave photography" },
      ],
    },
    bestFor: [
      { label: "Dramatic sandstone bluffs from the open beach", icon: "Camera" },
      { label: "Quiet, scenic beach walks", icon: "Sun" },
      { label: "Surf fishing and wildlife watching", icon: "Fish" },
    ],
    parkingSnapshot: {
      cost: "$12 weekdays / $15–25 weekends at the state beach lot",
      bestLot: "State beach day-use lot at the bottom of N Torrey Pines Rd",
      backupLot: "Free residential parking uphill off Del Mar Scenic Pkwy",
      fillsBy: "9am weekends; arrive early for the open beach lots",
      notes: "The North and South beach lots remain open. The park road and both upper reserve lots are closed through February 2027.",
    },
    dining: [
      { slug: "zel-s-del-mar", reason: "Low-key Del Mar pub with a breezy upstairs deck", distance: "6-min drive" },
      { slug: "board-brew", reason: "Legendary secret-sauce sandwiches and craft beer", distance: "7-min drive" },
      { slug: "jake-s-del-mar", reason: "Oceanfront seafood and steaks 5 minutes north", distance: "7-min drive" },
      { slug: "sammy-s-woodfired-pizza", reason: "Family-friendly wood-fired pizza and big salads", distance: "7-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/d779d503-62c1-4e3b-a07a-834a3e9c3d32/torrey-pines-0.jpg", alt: "Sandstone bluffs rising above Torrey Pines State Beach", caption: "The reserve's eroded bluffs are the most dramatic backdrop on the San Diego coast." },
      { src: "/__l5e/assets-v1/8d50ad3e-12d9-4c95-8970-a689d1ea6c66/torrey-pines-1.jpg", alt: "Wide beach at Torrey Pines at low tide", caption: "Low tide opens a long stretch of sand below the Beach Trail." },
      { src: "/__l5e/assets-v1/00313eb6-98b5-43c5-90f3-643f389fed76/torrey-pines-2.jpg", alt: "Torrey pine tree against coastal sky", caption: "The rare Torrey pine only grows here and on Santa Rosa Island." },
    ],
    nearbyAttractions: [
      { to: "/guides/best-san-diego-tide-pools", label: "Best San Diego Tide Pools", hint: "Small pools at the north end at low tide" },
      { to: "/guides/whale-watching-san-diego", label: "Whale Watching San Diego", hint: "Gray whales pass close to these bluffs December–April" },
      { to: "/beaches/del-mar-city-beach", label: "Del Mar City Beach", hint: "Dog-friendly sand just north of the state park" },
      { to: "/trails", label: "Coastal Trails", hint: "Bluff-top walks connecting the reserve to the beach" },
    ],
  },

  "sunset-cliffs": {
    coords: { lat: 32.7267, lng: -117.2547 },
    tideStationId: SAN_DIEGO_BAY,
    gettingThere: {
      driving:
        "From I-8, exit Sunset Cliffs Blvd south and follow it to the end. The cliff-top parking runs the length of Sunset Cliffs Natural Park from Ladera St to Cabrillo Monument.",
      parking:
        "Free curbside parking the entire length of Sunset Cliffs Blvd. Fills 30–45 minutes before sunset year-round. Ladera Street lot has the most turnover.",
      transit:
        "MTS Route 923 runs Voltaire St to Ocean Beach; walk 10 minutes west. Bike lane on Sunset Cliffs Blvd.",
      tips: [
        "Arrive 90 minutes before sunset for a spot and time to walk south along the trail.",
        "The stairs at Ladera St and at Osprey St give the only easy sand access at low tide.",
      ],
    },
    safety: {
      rip: "Not applicable at cliff-top; strong rip and shore-pound at any beach access. Do not swim here — treat it as a scenic overlook.",
      hazards: [
        "Cliffs erode constantly — stay behind fencing, never sit on the edge",
        "Beach access disappears at high tide — check the tide chart before heading down",
        "No lifeguards, no restrooms below the bluff, spotty cell reception",
      ],
      lifeguard: "None on the cliffs. Nearest towers are at Ocean Beach (1 mile north).",
      waterQuality: "Rarely tested (no swimming beach). Runoff pours over the cliffs after rain.",
    },
    faqs: [
      { q: "When is the best time to visit Sunset Cliffs?", a: "Arrive 90 minutes before sunset — you'll get a parking spot and time to walk the cliff-top trail. The 15 minutes after the sun drops (nautical twilight) are the most colorful and least crowded." },
      { q: "Can you swim at Sunset Cliffs?", a: "No. There is no lifeguarded beach and the shoreline is jagged reef and shore-break with strong rips. Beach access at Ladera or Osprey Street is for tide-pool exploration at low tide only — never enter the water." },
      { q: "Where can I see the Sunset Cliffs sea arches?", a: "The two most iconic arches are near the south end below the trails off Ladera St and near Sunset Cliffs Park. Both are only visible at tides of 1.0 ft or lower — check a tide chart before hiking down." },
      { q: "Is Sunset Cliffs free?", a: "Yes. Parking, cliff-top trails, and access to the natural park are all free 24/7. There are no entry fees, meters, or gates." },
      { q: "Are dogs allowed at Sunset Cliffs?", a: "Yes, on leash, along the cliff-top trails and grass. Not allowed on any sand beach access below." },
    ],
    activities: [
      { kind: "sunset", rating: "great", note: "SD's #1 sunset spot" },
      { kind: "photo", rating: "great" },
      { kind: "tidepool", rating: "great", note: "Low tide at Ladera/Osprey" },
      { kind: "dogs", rating: "good", note: "Leashed on cliff-top only" },
      { kind: "picnic", rating: "good", note: "Cliff-top grass" },
      { kind: "swim", rating: "avoid", note: "Jagged reef, no lifeguards" },
      { kind: "surf", rating: "fair", note: "Experts only" },
      { kind: "bonfire", rating: "avoid" },
    ],
    packing: {
      bring: [
        "Warm layer — cliffs cool off fast after sunset",
        "Headlamp for the walk back to the car",
        "Tide chart if heading down to the pools",
        "Sturdy shoes — reef is sharp",
        "Camera + wide lens",
      ],
      rentNearby: [
        "Not much on-site — head to Ocean Beach for gear",
        "Bike rentals in OB village",
      ],
      skip: [
        "Flip-flops on the reef",
        "Swimsuits — this isn't a swim spot",
        "Drones inside protected airspace (check current rules)",
      ],
    },
    photoGuide: {
      bestTime:
        "Sunset year-round; December sun sets the furthest south — the arches align for silhouette shots.",
      bestSpots: [
        "Sea arches at low tide (Ladera St stairs)",
        "Cliff at Osprey St for wave-crash blowholes",
        "Sunset Cliffs Park south end (grass + horizon)",
      ],
      tips: [
        "Stay 15 min after sunset — the sky turns pink long after the sun drops.",
        "Use a tripod at blue hour for silky-water long exposures.",
        "Never step past the fencing — sandstone cliff edges break off constantly.",
      ],
    },
    seasonalCalendar: {
      summary:
        "A year-round sunset destination — winter delivers the most colorful skies and biggest wave action.",
      rows: {
        crowd: [2, 2, 2, 2, 2, 3, 3, 3, 3, 2, 2, 2],
        water: WATER_TEMP_SD,
        surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3],
        wildlife: [3, 3, 2, 1, 1, 1, 1, 1, 1, 2, 2, 3],
        weather: SUN_SD,
      },
      events: [
        { months: "Dec–Apr", label: "Gray whale spouts visible from the bluff" },
        { months: "Nov–Feb", label: "Biggest sunsets & post-storm surf" },
        { months: "Jun–Sep", label: "Warm evenings, peak crowds at Ladera St" },
      ],
    },
    bestFor: [
      { label: "Sunset photography and cliff-top walks", icon: "Camera" },
      { label: "Low-tide tide pool exploration", icon: "Fish" },
      { label: "Dramatic winter surf watching", icon: "Waves" },
    ],
    parkingSnapshot: {
      cost: "Free curbside parking along Sunset Cliffs Blvd",
      bestLot: "Ladera Street lot — highest turnover, closest to the sea arches",
      backupLot: "Free street parking north of Osprey St or south of Point Loma Ave",
      fillsBy: "60–90 minutes before sunset year-round",
      notes: "No meters, no gates, and no restrooms — plan accordingly before arriving.",
    },
    dining: [
      { slug: "point-loma-seafoods", reason: "Counter-service seafood eaten over the marina since 1963", distance: "7-min drive" },
      { slug: "south-beach-bar-grille", reason: "The OB pier-end institution for mahi tacos and a pint", distance: "5-min drive" },
      { slug: "hodad-s", reason: "Iconic OB burger shack 5 minutes north on Newport Ave", distance: "5-min drive" },
      { slug: "ob-noodle-house", reason: "Steaming pho and the cheapest happy hour in OB", distance: "6-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/a64e9276-f975-4cf9-b59d-b69ee529f63e/sunset-cliffs-0.jpg", alt: "Sunset Cliffs sea arch at sunset", caption: "The sea arches glow at low tide during the last light." },
      { src: "/__l5e/assets-v1/9d000624-8d6b-4312-b91b-a241044a39f4/sunset-cliffs-1.jpg", alt: "Waves crashing against Sunset Cliffs bluffs", caption: "Winter swells send spray high against the sandstone." },
      { src: "/__l5e/assets-v1/3559ad28-84d3-4b36-9d17-c371eb75ceab/sunset-cliffs-2.jpg", alt: "Tide pools exposed at low tide below Sunset Cliffs", caption: "Low tide reveals pools filled with anemones and urchins." },
    ],
    nearbyAttractions: [
      { to: "/guides/best-san-diego-tide-pools", label: "Best San Diego Tide Pools", hint: "Where to find the best low-tide pools below the cliffs" },
      { to: "/beaches/ocean-beach", label: "Ocean Beach", hint: "Classic surf town 1 mile north" },
      { to: "/guides/san-diego-bioluminescence", label: "Bioluminescence Guide", hint: "Red tides and nighttime glow events" },
      { to: "/surf", label: "Live Surf Report", hint: "Check swell and conditions for the Point Loma coast" },
    ],
  },

  "windansea-beach": {
    coords: { lat: 32.832, lng: -117.281 },
    tideStationId: LA_JOLLA,
    gettingThere: {
      driving:
        "From I-5, exit La Jolla Pkwy west, right on La Jolla Blvd, then right on Nautilus St which dead-ends at the beach.",
      parking:
        "Free residential street parking on Neptune Pl and Nautilus St. No lot. Fills by 8am on swell days and every summer weekend.",
      transit: "MTS Route 30 stops at La Jolla Blvd & Nautilus, 3-min walk down.",
      tips: [
        "Park on Playa del Sur (2 blocks south) when Neptune Pl is full — quieter path down.",
        "No restrooms — use the ones at La Jolla Rec Center (0.5 mi east) before you head over.",
      ],
    },
    safety: {
      rip: "High. Windansea is a reef break with fast, unpredictable currents. Not a swimming beach — only experienced surfers should enter the water.",
      hazards: [
        "Shallow reef and sharp rocks at the takeoff — wear a leash and know the entry",
        "Localism is real — the lineup is known for regulars who protect the peak",
        "No lifeguards outside summer weekends",
      ],
      lifeguard:
        "Seasonal tower staffed weekends spring/fall and daily May–September. Emergency: 619-221-8899.",
      waterQuality:
        "Generally excellent — reef bottom flushes well. Advisories after heavy rain are common in La Jolla.",
    },
    faqs: [
      { q: "Can beginners surf at Windansea?", a: "No. Windansea is a fast, shallow reef break with a heavy local lineup. First-time surfers should go to La Jolla Shores or Pacific Beach instead — both are 10 minutes away and infinitely more forgiving." },
      { q: "Is Windansea good for swimming?", a: "Not really. The bottom is reef and rock, there's no lifeguarded swimming area, and rip currents run strong. It's better as a tide-pool and sunset beach than a swim spot." },
      { q: "What's the story behind the Windansea shack?", a: "The palm-thatched shack at the top of the beach was built in 1946 by returning WWII sailors and has been rebuilt several times after storms. It's a designated San Diego Historical Landmark and one of the most photographed structures on the California coast." },
      { q: "Where can I park at Windansea?", a: "Free residential street parking on Neptune Pl, Nautilus St, and Playa del Sur. There's no lot. Arrive before 8am on weekends and swell days or you'll circle for 20 minutes." },
      { q: "Are dogs allowed at Windansea?", a: "Yes, on leash. Off-leash is not permitted. There's no dog-specific area — most owners walk the sand north toward Big Rock at low tide." },
    ],
    activities: [
      { kind: "surf", rating: "great", note: "Experts only" },
      { kind: "sunset", rating: "great", note: "The famous shack silhouette" },
      { kind: "photo", rating: "great" },
      { kind: "tidepool", rating: "good", note: "North toward Big Rock at low tide" },
      { kind: "dogs", rating: "good", note: "On-leash" },
      { kind: "picnic", rating: "fair", note: "No tables, sand only" },
      { kind: "swim", rating: "avoid", note: "Reef + rip currents" },
      { kind: "snorkel", rating: "avoid" },
      { kind: "bonfire", rating: "avoid" },
    ],
    packing: {
      bring: [
        "Reef booties if you're surfing",
        "Wetsuit (3/2 year-round, 4/3 in winter)",
        "Everything else — no restrooms, no showers, no snack bar",
        "Cash for meter-free parking (bring nothing — just don't lose it)",
      ],
      rentNearby: [
        "Boards & wetsuits at Mitch's Surf Shop (5 min drive)",
        "Coffee & burritos at Bird Rock Coffee (5 min drive)",
      ],
      skip: [
        "Beginner surf lessons — go to LJ Shores instead",
        "Glass, alcohol, off-leash dogs",
        "Any expectation of lifeguards outside summer weekends",
      ],
    },
    photoGuide: {
      bestTime:
        "Sunset — the shack silhouettes perfectly against the sun from October through February.",
      bestSpots: [
        "Directly in front of the Windansea shack",
        "North rocks at low tide for wave + reef frames",
        "Neptune Pl overlook for the elevated angle",
      ],
      tips: [
        "Line up the shack roof with the horizon at sunset for the classic silhouette.",
        "Bring a telephoto to shoot surfers on the reef from the sand — you don't need to swim out.",
      ],
    },
    seasonalCalendar: {
      summary:
        "A surfer's beach with a photographer's sunset. Winter is prime swell; September delivers warm-water evening sessions.",
      rows: {
        crowd: [1, 1, 1, 2, 2, 2, 3, 3, 2, 2, 1, 1],
        water: WATER_TEMP_SD,
        surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3],
        wildlife: [2, 2, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2],
        weather: SUN_SD,
      },
      events: [
        { months: "Nov–Mar", label: "Peak winter swell — biggest days at the reef" },
        { months: "Dec–Apr", label: "Gray whales offshore" },
        { months: "Jul", label: "WindanSea Surf Club menehune contest" },
      ],
    },
  
    bestFor: [
      { label: "Expert Surfing", icon: "Waves" },
      { label: "Sunset Photography", icon: "Camera" },
      { label: "Tide Pooling", icon: "Fish" }
    ],
    parkingSnapshot: {
      cost: "Free",
      bestLot: "Street parking on Neptune Pl & Bonair St",
      backupLot: "Residential streets further east",
      fillsBy: "9 AM on weekends, 11 AM weekdays",
      notes: "Limited street parking, arrive early for best spots."
    },
    dining: [
      { slug: "the-taco-stand", reason: "Tijuana-style tacos five minutes up Pearl St", distance: "5-min drive" },
      { slug: "puesto-la-jolla", reason: "Street tacos and cocktails in the village", distance: "6-min drive" },
      { slug: "cody-s-la-jolla", reason: "Ocean-air breakfast patio before a morning session", distance: "6-min drive" },
      { slug: "george-s-at-the-cove", reason: "Iconic La Jolla oceanfront patios for a splurge dinner", distance: "7-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/2aec4e7b-21cc-4afc-b749-ab5760ec19d4/windansea-beach-0.jpg", alt: "Iconic Windansea Beach shack at sunset with waves crashing", caption: "The historic Windansea surf shack bathed in golden hour light." },
      { src: "/__l5e/assets-v1/c6e80d76-bc2e-46ad-9484-db4765a8cab2/windansea-beach-1.jpg", alt: "Surfer riding a powerful wave at Windansea Beach", caption: "A skilled surfer navigating the challenging reef break at Windansea." },
      { src: "/__l5e/assets-v1/b2056ffa-4398-4dfb-8f76-e53654894c48/windansea-beach-2.jpg", alt: "Colorful tide pools at Windansea Beach during low tide", caption: "Vibrant marine life visible in the tide pools at Big Rock." }
    ],
    nearbyAttractions: [
      { to: "/beaches/la-jolla-cove", label: "La Jolla Cove", hint: "Swim with seals and snorkel in clear waters." },
      { to: "/guides/things-to-do-in-la-jolla", label: "La Jolla Village", hint: "Upscale boutiques, art galleries, and diverse dining options." },
      { to: "/surf", label: "Surf Lessons", hint: "Learn to surf at more beginner-friendly spots nearby." },
      { to: "/tides", label: "Tide Charts", hint: "Check low tide for best tide pooling conditions." }
    ],
  },

  "pacific-beach": {
    tidePool: {
      spot: "Best pools: the reef at the north end of PB, from Tourmaline to False Point (Law Street stairs north).",
      note: "Most of Pacific Beach is sand. The rocky shelf starts where the beach meets the La Jolla bluffs — anemones, hermit crabs and sea hares appear on lows around 0 ft and below.",
      link: { to: "/guides/mission-beach-vs-ocean-beach-vs-pacific-beach", label: "Mission vs Ocean vs Pacific Beach guide" },
    },
    gettingThere: {
      driving: "From I-5, exit Grand Ave/Garnet Ave west and follow Garnet to Mission Blvd. Crystal Pier is at the foot of Garnet.",
      parking: "No large free beach lot. Free 2-hour and unrestricted residential streets east of Mission Blvd; paid lots near Crystal Pier ($10–20/day). Tourmaline's free lot is the best backup at the north end.",
      transit: "MTS Route 8 runs along Mission Blvd; Route 30 connects PB with Old Town and La Jolla.",
      tips: ["Arrive before 10am on summer weekends.", "Read curb signs carefully — tow-away and street-sweeping zones are strictly enforced."],
    },
    safety: {
      rip: "Rip currents form next to Crystal Pier and along sandbar gaps. Swim in front of a staffed tower.",
      hazards: ["Crowded surf-school zones in summer mornings", "Alcohol is banned on the beach and boardwalk", "Boardwalk speed limit is 8 mph"],
      lifeguard: "Year-round San Diego lifeguard towers, main station near the foot of Grand Ave.",
    },
    faqs: [
      { q: "Is Pacific Beach safe?", a: "Yes, during the day it's one of San Diego's busiest, most-patrolled beaches, with lifeguards year-round. Swim near a tower, watch for rips by Crystal Pier, and expect a loud bar scene on Garnet Ave at night." },
      { q: "Where can I park for free at Pacific Beach?", a: "Residential streets east of Mission Blvd and north of Garnet are free but fill by 10am on weekends. The Tourmaline Surfing Park lot at the north end is free and a short walk to the sand." },
      { q: "Are there tide pools at Pacific Beach?", a: "Yes, at the north end. The reef from Tourmaline to False Point is exposed at low tides around 0 ft or lower." },
      { q: "Are dogs allowed at Pacific Beach?", a: "Not on the sand or boardwalk 9am–6pm (8am–8pm in summer). Head to OB Dog Beach for off-leash time." },
    ],
    coords: { lat: 32.7998, lng: -117.2559 },
    tideStationId: SAN_DIEGO_BAY,
    activities: [
      { kind: "surf", rating: "great", note: "Beginner-friendly beachbreak" },
      { kind: "swim", rating: "great" },
      { kind: "bike", rating: "great", note: "3.2 mi boardwalk" },
      { kind: "sunset", rating: "great" },
      { kind: "volleyball", rating: "good", note: "Nets north of pier" },
      { kind: "picnic", rating: "good" },
      { kind: "dogs", rating: "avoid", note: "Not allowed on beach 9am–6pm" },
      { kind: "bonfire", rating: "fair", note: "Fire rings near Crystal Pier" },
      { kind: "snorkel", rating: "fair" },
    ],
    packing: {
      bring: ["Beach cruiser or skates", "Sunscreen", "Cash for boardwalk food", "Boardshorts / swimsuit"],
      rentNearby: ["Boards & bikes at Cheap Rentals (Mission Blvd)", "Surf lessons at Pacific Surf School", "SUPs at Mission Bay Sportcenter"],
      skip: ["Glass & alcohol", "Off-leash dogs", "Driving in — parking is brutal after 10am"],
    },
    photoGuide: {
      bestTime: "Golden hour at Crystal Pier — the pier silhouettes into the sun Oct–Feb.",
      bestSpots: ["Crystal Pier walkway", "Tourmaline overlook to the north", "Boardwalk at Reed Ave for the palm-tree frame"],
      tips: ["Shoot from the sand looking up at the pier pilings.", "Bring a wide lens for boardwalk-life street shots."],
    },
    seasonalCalendar: {
      summary: "Year-round social beach. Peak scene July–August; September has the best water and lighter crowds.",
      rows: { crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_SD },
      events: [
        { months: "Jul", label: "Over-the-Line tournament at Fiesta Island (nearby)" },
        { months: "Aug", label: "Peak nightlife on Garnet Ave" },
      ],
    },
    bestFor: [
      { label: "Learning to surf and boardwalk cruising", icon: "Waves" },
      { label: "Beach volleyball and social scene", icon: "Users" },
      { label: "Crystal Pier photography at golden hour", icon: "Camera" },
    ],
    parkingSnapshot: {
      cost: "Free street parking + $10–15 paid lots near the pier",
      bestLot: "Mission Blvd north of Garnet (free, fills by 10am)",
      backupLot: "Paid lots near Crystal Pier or side streets south of Reed Ave",
      fillsBy: "10am weekends; earlier on sunny summer days",
      notes: "Biking in from Mission Bay or Pacific Beach avoids the parking hunt entirely.",
    },
    dining: [
      { slug: "kono-s-cafe", reason: "Enormous breakfast burritos eaten on the seawall", distance: "1-min walk" },
      { slug: "oscar-s-mexican-seafood", reason: "Locals' choice for fish tacos on Turquoise St", distance: "6-min drive" },
      { slug: "taco-surf-pb", reason: "Foil-wrapped carne asada and rolled tacos on Mission Blvd", distance: "5-min walk" },
      { slug: "the-fishery", reason: "Pacific Beach seafood institution on Cass St", distance: "4-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/38c7efad-5629-427e-8c93-895913ee98dd/pacific-beach-0.jpg", alt: "Crystal Pier at Pacific Beach at sunset", caption: "The 1927 pier is the visual anchor of PB." },
      { src: "/__l5e/assets-v1/939c72aa-e8ba-4725-9168-962b87dd6a1e/pacific-beach-1.jpg", alt: "Busy boardwalk at Pacific Beach with palm trees", caption: "The 3.2-mile boardwalk is San Diego's unofficial main street." },
      { src: "/__l5e/assets-v1/aca73ace-d16d-4070-b633-b03ab51e8047/pacific-beach-2.jpg", alt: "Surfers in the lineup at Pacific Beach", caption: "Soft, predictable waves make PB the city's learn-to-surf classroom." },
    ],
    nearbyAttractions: [
      { to: "/beaches/tourmaline", label: "Tourmaline Surf Park", hint: "Longboard-only surf beach at the north end of PB" },
      { to: "/guides/best-surf-spots-by-skill-level", label: "Best Surf Spots by Skill", hint: "Why PB is perfect for beginners" },
      { to: "/dining", label: "San Diego Dining Guide", hint: "Garnet Ave tacos, seafood, and happy hours" },
      { to: "/beaches/mission-beach", label: "Mission Beach", hint: "Boardwalk continues south to Belmont Park" },
    ],
  },

  "tourmaline": {
    coords: { lat: 32.807, lng: -117.2625 },
    tideStationId: SAN_DIEGO_BAY,
    activities: [
      { kind: "surf", rating: "great", note: "Longboard heaven" },
      { kind: "sunset", rating: "great" },
      { kind: "photo", rating: "good" },
      { kind: "picnic", rating: "good", note: "Grassy bluff above" },
      { kind: "dogs", rating: "fair", note: "Leashed on bluff only" },
      { kind: "swim", rating: "avoid", note: "Surfing-only city beach" },
      { kind: "bonfire", rating: "avoid" },
    ],
    packing: {
      bring: ["Longboard or funshape", "Wetsuit (3/2 year-round)", "Coffee — cliff-top sunrise is the ritual", "Change of clothes"],
      rentNearby: ["Boards at Mitch's Surf Shop (5 min drive)", "Coffee at Better Buzz on Mission Blvd"],
      skip: ["Bodyboards & swimmers (not allowed in lineup)", "Shortboarding etiquette-breakers", "Glass"],
    },
    photoGuide: {
      bestTime: "Sunrise for empty lineup + glassy water; sunset for silhouette longboarders.",
      bestSpots: ["Grass bluff above the lot", "North end sand at low tide", "Playa del Sur overlook"],
      tips: ["Telephoto from the bluff catches surfer silhouettes against sunset.", "Low tide exposes reef rocks for foreground interest."],
    },
    seasonalCalendar: {
      summary: "Consistent longboarding all year. Winter NW swells produce the best waves; September delivers warm-water dawn sessions.",
      rows: { crowd: [1, 1, 1, 2, 2, 2, 3, 3, 2, 1, 1, 1], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_SD },
      events: [{ months: "Year-round", label: "Sunset longboard sessions with the regulars" }],
    },
  
    bestFor: [
      { label: "Longboard Surfing", icon: "Waves" },
      { label: "Sunset Views", icon: "Sun" },
      { label: "Dog Walking", icon: "Dog" }
    ],
    parkingSnapshot: {
      cost: "Free",
      bestLot: "Tourmaline Surfing Park parking lot",
      backupLot: "Street parking on Tourmaline St & La Jolla Blvd",
      fillsBy: "10 AM on weekends, 12 PM weekdays",
      notes: "Dedicated lot fills quickly, residential street parking is an option."
    },
    dining: [
      { slug: "kono-s-cafe", reason: "Breakfast burrito run before the dawn session", distance: "5-min drive" },
      { slug: "oscar-s-mexican-seafood", reason: "Fish tacos and mariscos two blocks up Turquoise", distance: "3-min drive" },
      { slug: "taco-surf-pb", reason: "Cheap post-surf burritos with surf posters on the wall", distance: "6-min drive" },
      { slug: "the-fishery", reason: "Seasonal local catch and a serious wine list in PB", distance: "6-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/97daa003-3b3d-4b9d-8b11-afb47f4888b7/tourmaline-0.jpg", alt: "Longboard surfers riding gentle waves at Tourmaline Surfing Park", caption: "A perfect day for longboarding at the iconic Tourmaline surf break." },
      { src: "/__l5e/assets-v1/a1962d6a-3898-4126-9845-faa74fd7deb8/tourmaline-1.jpg", alt: "Sunset over Tourmaline Surfing Park with people on the bluff", caption: "Spectacular sunset painting the sky above the grassy bluff." },
      { src: "/__l5e/assets-v1/c786c87b-e63c-400f-a5b9-201a8ef196c4/tourmaline-2.jpg", alt: "Dog walker enjoying the view from the bluff at Tourmaline", caption: "A popular spot for dog owners to enjoy ocean views." }
    ],
    nearbyAttractions: [
      { to: "/beaches/pacific-beach", label: "Pacific Beach", hint: "Lively boardwalk, pier, and various beach activities." },
      { to: "/dining", label: "PB Nightlife", hint: "Bars and entertainment for a vibrant evening out." },
      { to: "/surf", label: "Surf Rentals", hint: "Rent a longboard and join the mellow waves." },
      { to: "/trails", label: "Coastal Walk", hint: "Scenic walk along the bluff towards La Jolla." }
    ],
  },

  "ocean-beach": {
    tidePool: {
      spot: "Best pools: the reef south of the pier, from Pescadero Ave down to Sunset Cliffs Natural Park.",
      note: "The main beach is sand, but just south of the pier the rock shelves begin. On lows near 0 ft and below you'll find anemones, sea stars and octopus in the Sunset Cliffs pools. Stay off the bluffs.",
      link: { to: "/guides/sunset-cliffs-tide-pools", label: "Sunset Cliffs tide-pool guide" },
    },
    gettingThere: {
      driving: "From I-8 west, stay on it until it becomes Sunset Cliffs Blvd, then turn right on Newport Ave to the beach.",
      parking: "Free lots at the foot of Newport Ave and at the end of Voltaire St by Dog Beach. Free street parking on Abbott, Bacon and side streets. No meters.",
      transit: "MTS Routes 35 and 923 serve Ocean Beach from Old Town Transit Center.",
      tips: ["Dog Beach lot fills first on weekends.", "Wednesday 4–8pm the farmers market closes Newport Ave."],
    },
    safety: {
      rip: "Strong rips form along the pier and near the river mouth; OB is a surf break more than a swimming beach.",
      hazards: ["Heavy winter shore-break", "Tijuana River or storm-drain advisories after rain", "Rocks under the pier pilings"],
      lifeguard: "Year-round tower at the foot of Santa Monica Ave.",
    },
    faqs: [
      { q: "Is Ocean Beach safe?", a: "Yes for beachgoers who swim near a lifeguard and respect the surf. The pier area has strong rips, and winter shore-break can be heavy. Newport Ave is busy and friendly by day; use normal city sense at night." },
      { q: "Where can I park for free at Ocean Beach?", a: "OB has free lots at the foot of Newport Ave and next to Dog Beach, plus free street parking. There are no parking meters." },
      { q: "Are there tide pools at Ocean Beach?", a: "Yes, south of the pier. The reef from Pescadero Ave to Sunset Cliffs is exposed on low tides near 0 ft and below." },
      { q: "Can dogs go off-leash at Ocean Beach?", a: "Yes, at Dog Beach on the north end, 24 hours a day." },
    ],
    coords: { lat: 32.7495, lng: -117.2528 },
    tideStationId: SAN_DIEGO_BAY,
    activities: [
      { kind: "dogs", rating: "great", note: "Off-leash 24/7 at Dog Beach" },
      { kind: "surf", rating: "great", note: "Rippy beachbreak — intermediate+" },
      { kind: "bonfire", rating: "great", note: "Fire rings on the beach" },
      { kind: "sunset", rating: "great", note: "OB Pier silhouette" },
      { kind: "fish", rating: "good", note: "OB Pier is free to fish" },
      { kind: "picnic", rating: "good" },
      { kind: "swim", rating: "fair", note: "Watch winter shore-break" },
      { kind: "snorkel", rating: "avoid" },
    ],
    packing: {
      bring: ["Firewood (no pallets)", "Leash-optional dog + poop bags", "Warm layer for after-sunset bonfire", "Cash for Wednesday farmers market"],
      rentNearby: ["Surfboards at South Coast Surf Shop", "Bike rentals on Newport Ave", "Wetsuits at OB Surf & Skate"],
      skip: ["Glass & alcohol on sand", "Drones near the pier", "Expecting quiet — this is a working surf town"],
    },
    photoGuide: {
      bestTime: "45 min before sunset from the pier looking back at Sunset Cliffs.",
      bestSpots: ["End of the OB Pier", "Dog Beach at low tide (dogs sprinting through surf)", "Newport Ave at dusk for neon signs"],
      tips: ["Walk to the end of the pier for the reverse city-back view.", "Wednesday farmers market 4–8pm makes great street photography."],
    },
    seasonalCalendar: {
      summary: "OB's vibe is constant. Fall brings the warmest water and best surf; winter storms produce dramatic pier waves.",
      rows: { crowd: [2, 2, 2, 2, 2, 3, 3, 3, 2, 2, 2, 2], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_SD },
      events: [
        { months: "Wednesdays", label: "OB Farmers Market on Newport Ave" },
        { months: "Jul 4", label: "OB Fireworks over the pier" },
      ],
    },
    bestFor: [
      { label: "Off-leash dogs at Dog Beach", icon: "Dog" },
      { label: "Surf culture and local character", icon: "Waves" },
      { label: "Beach bonfires and pier fishing", icon: "Flame" },
    ],
    parkingSnapshot: {
      cost: "Free lots at Newport Ave and near Dog Beach",
      bestLot: "Lot at the foot of Newport Ave (central beach access)",
      backupLot: "Dirt lot near Dog Beach or residential streets east of Bacon St",
      fillsBy: "11am weekends; easier than most San Diego beaches",
      notes: "Wednesday afternoons the farmers market closes Newport Ave 4–8pm.",
    },
    dining: [
      { slug: "hodad-s", reason: "The OB institution for burgers since 1969", distance: "4-min walk" },
      { slug: "south-beach-bar-grille", reason: "Mahi tacos and cold pints at the foot of Newport Ave", distance: "2-min walk" },
      { slug: "ob-noodle-house", reason: "Pho, banh mi and a legendary cheap happy hour", distance: "6-min walk" },
      { slug: "point-loma-seafoods", reason: "Fresh fish sandwiches over the marina, 10 minutes east", distance: "10-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/60f27191-9746-488a-8b64-1df69af29789/ocean-beach-0.jpg", alt: "Ocean Beach Pier extending into the Pacific", caption: "The longest concrete pier on the West Coast." },
      { src: "/__l5e/assets-v1/a347a350-9f4f-4fca-8859-88ad6562db33/ocean-beach-1.jpg", alt: "Dogs playing in the surf at OB Dog Beach", caption: "Dog Beach is open off-leash 24 hours a day." },
      { src: "/__l5e/assets-v1/9fcd7fff-cbf8-4f0a-90dd-5d42d0e40711/ocean-beach-2.jpg", alt: "Colorful shops along Newport Avenue in Ocean Beach", caption: "Newport Ave is three blocks of independent surf shops and cafés." },
    ],
    nearbyAttractions: [
      { to: "/guides/ocean-beach-dog-beach-guide", label: "OB Dog Beach Guide", hint: "Rules, parking, and the best times to visit" },
      { to: "/guides/post-surf-eats", label: "Post-Surf Eats", hint: "Where locals eat after a session" },
      { to: "/beaches/sunset-cliffs", label: "Sunset Cliffs", hint: "Dramatic bluffs 1 mile south" },
      { to: "/guides/best-bonfire-beaches-san-diego", label: "Best Bonfire Beaches", hint: "OB has some of the best fire rings in the city" },
    ],
  },

  "mission-beach": {
    tidePool: {
      spot: "Nearest pools: north end of Pacific Beach (Tourmaline to False Point), about 10 minutes by car, or the Sunset Cliffs reef south of Ocean Beach.",
      note: "Mission Beach is all sand with no reef, so there are no tide pools here. A low tide still gives you the widest beach for walking and volleyball, and these are the best daylight lows for a quick trip to the nearby pools.",
      link: { to: "/guides/mission-beach-vs-ocean-beach-vs-pacific-beach", label: "Mission vs Ocean vs Pacific Beach guide" },
    },
    gettingThere: {
      driving: "From I-8 west, exit W Mission Bay Dr, cross the bridge and follow it to Mission Blvd. Belmont Park is at the corner.",
      parking: "Free Belmont Park lot and Mission Bay lots at Bonita Cove and Ventura Cove (fill by 10am weekends). Free street parking on the courts and places off Mission Blvd, with 2-hour limits in some zones.",
      transit: "MTS Route 8 runs from Old Town along Mission Blvd.",
      tips: ["Bonita Cove lot on the bay side is the best backup.", "Walk-streets have no parking — don't try."],
    },
    safety: {
      rip: "Rip currents are common, especially near the South Mission jetty. Swim in front of a lifeguard tower.",
      hazards: ["Shore-break at the Wedge (south end)", "Crowded boardwalk with bikes and skaters", "Alcohol banned on beach and boardwalk"],
      lifeguard: "Year-round towers; main station at Ventura Place.",
    },
    faqs: [
      { q: "Is Mission Beach safe?", a: "Yes, it's heavily patrolled with lifeguards year-round. Swim near a tower, avoid the jetty at the south end, and watch for bikes on the boardwalk." },
      { q: "Where can I park for free at Mission Beach?", a: "Belmont Park's lot is free, as are the bay-side lots at Bonita Cove and Ventura Cove. All fill by about 10am on summer weekends." },
      { q: "Are there tide pools at Mission Beach?", a: "No, the beach is sand. The closest tide pools are at the north end of Pacific Beach near Tourmaline, or at Sunset Cliffs south of Ocean Beach." },
      { q: "Mission Beach or Pacific Beach, which is better?", a: "Mission Beach is better for families, Belmont Park and calm bay water. Pacific Beach is better for surf lessons, Crystal Pier and nightlife. The boardwalk connects them." },
    ],
    coords: { lat: 32.7707, lng: -117.2525 },
    tideStationId: MISSION_BAY,
    activities: [
      { kind: "bike", rating: "great", note: "3+ mi boardwalk" },
      { kind: "swim", rating: "great" },
      { kind: "surf", rating: "good", note: "Beachbreak, all levels" },
      { kind: "volleyball", rating: "great", note: "Permanent nets" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good" },
      { kind: "sup", rating: "good", note: "Calmer water on the Mission Bay side" },
      { kind: "dogs", rating: "avoid", note: "Not on sand 9am–6pm summer" },
      { kind: "bonfire", rating: "good" },
    ],
    packing: {
      bring: ["Beach cruiser or skates", "Volleyball if you want your own", "Firewood for the pits", "Sunscreen"],
      rentNearby: ["Bikes/skates at Cheap Rentals & Mission Beach Rentals", "SUPs at Mission Bay Sportcenter", "Surfboards at Pacific Surf School"],
      skip: ["Glass, alcohol on sand", "Off-leash dogs summer", "Driving in — the traffic is legendary"],
    },
    photoGuide: {
      bestTime: "Sunset from the boardwalk near Belmont Park — the coaster is the frame.",
      bestSpots: ["Belmont Park Giant Dipper coaster", "Boardwalk south of Ventura Cove", "Mission Bay side at blue hour"],
      tips: ["Long exposure catches coaster light trails.", "Golden hour on the bay side is calmer & bluer than ocean side."],
    },
    seasonalCalendar: {
      summary: "The classic SD beach summer. July–August is a scene; May–June is quiet with cool water; October rewards patience.",
      rows: { crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_SD },
      events: [{ months: "Jul", label: "Peak Belmont Park summer + fireworks" }],
    },
    bestFor: [
      { label: "Boardwalk biking and roller skating", icon: "Bike" },
      { label: "Belmont Park and family amusements", icon: "Users" },
      { label: "Beach volleyball and bay-side paddleboarding", icon: "Waves" },
    ],
    parkingSnapshot: {
      cost: "Free street south of Belmont; paid lots $10–20 near the park",
      bestLot: "Mission Bay side near Bonita Cove (free, then walk 5 min)",
      backupLot: "Residential streets south of Ventura Cove",
      fillsBy: "10am weekends; Belmont Park lots fill first",
      notes: "The smartest move is to bike in along the boardwalk from Pacific Beach.",
    },
    dining: [
      { slug: "the-mission", reason: "The beach-town brunch benchmark a block off the boardwalk", distance: "4-min walk" },
      { slug: "luigi-s-at-the-beach", reason: "Paper-plate pizza and wings right off the boardwalk", distance: "3-min walk" },
      { slug: "kono-s-cafe", reason: "Breakfast burritos by Crystal Pier, worth the line", distance: "8-min drive" },
      { slug: "the-fishery", reason: "PB seafood a short drive north on Cass St", distance: "8-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/c964e249-2932-4993-b676-66a1b3ec18ae/mission-beach-0.jpg", alt: "Belmont Park Giant Dipper roller coaster at Mission Beach", caption: "The wooden Giant Dipper has been running since 1925." },
      { src: "/__l5e/assets-v1/be2283d9-e9b5-4580-8563-996d5b1ea840/mission-beach-1.jpg", alt: "Wide sandy beach at Mission Beach with boardwalk", caption: "The two-mile strand sits between the ocean and Mission Bay." },
      { src: "/__l5e/assets-v1/0b3e5bdb-5de6-4fdd-b6e5-86252dcbd470/mission-beach-2.jpg", alt: "Sunset over Mission Bay from Mission Beach", caption: "The bay side delivers calmer water and bluer sunsets." },
    ],
    nearbyAttractions: [
      { to: "/beaches/pacific-beach", label: "Pacific Beach", hint: "The boardwalk continues north with more surf breaks" },
      { to: "/guides/mission-beach-boardwalk", label: "Mission Beach Boardwalk", hint: "Biking, rentals, and the full loop" },
      { to: "/guides/best-beaches-in-san-diego", label: "Best Beaches in San Diego", hint: "How Mission Beach compares to the coast" },
      { to: "/dining", label: "San Diego Dining Guide", hint: "Tacos and boardwalk bites nearby" },
    ],
  },

  "powerhouse-park": {
    coords: { lat: 32.9587, lng: -117.2717 },
    tideStationId: LA_JOLLA,
    activities: [
      { kind: "picnic", rating: "great", note: "Grassy park above the sand" },
      { kind: "sunset", rating: "great" },
      { kind: "photo", rating: "great" },
      { kind: "swim", rating: "good" },
      { kind: "surf", rating: "good", note: "Del Mar beachbreak" },
      { kind: "dogs", rating: "fair", note: "Off-leash at north end Oct–May" },
      { kind: "bike", rating: "good", note: "Coastal Rail Trail nearby" },
    ],
    packing: {
      bring: ["Picnic blanket", "Frisbee / paddle set", "Layers — Del Mar cools off fast", "Reef-safe SPF"],
      rentNearby: ["Bikes on Coast Blvd", "Boards at Del Mar Surf Co (short drive)"],
      skip: ["Off-leash dogs May–Sep (main beach only)", "Glass, alcohol"],
    },
    photoGuide: {
      bestTime: "Sunset from the grassy bluff — palm trees frame the horizon.",
      bestSpots: ["Powerhouse Park lawn", "Sand at 15th St stairs", "Del Mar Bluff Trail north of the park"],
      tips: ["Frame the sunset between palm trees for a tropical look.", "Blue hour is 20 min after sunset — sky turns cobalt."],
    },
    seasonalCalendar: {
      summary: "Del Mar's flagship family park. September has warm water + no crowds; racing season (Jul–Sep) fills the town.",
      rows: { crowd: [1, 1, 1, 2, 2, 2, 3, 3, 2, 1, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2], weather: SUN_NORTH },
      events: [
        { months: "Jul–Sep", label: "Del Mar Racetrack season — town fills up" },
        { months: "Jun", label: "San Diego County Fair overflow crowds" },
      ],
    },
  
    bestFor: [
      { label: "Family Picnics", icon: "Users" },
      { label: "Beach Access", icon: "Bath" },
      { label: "Sunset Watching", icon: "Sun" }
    ],
    parkingSnapshot: {
      cost: "Metered",
      bestLot: "Powerhouse Park parking lot",
      backupLot: "Street parking on Coast Blvd",
      fillsBy: "9 AM on weekends, 11 AM weekdays",
      notes: "Metered parking, can be challenging to find spots on busy days."
    },
    dining: [
      { slug: "jake-s-del-mar", reason: "Oceanfront dining with classic California fare, steps from the park", distance: "2-min walk" },
      { slug: "board-brew", reason: "Stacked sandwiches and craft beer for a picnic on the lawn", distance: "5-min walk" },
      { slug: "zel-s-del-mar", reason: "Neighborhood pub with a deck catching the sea breeze", distance: "4-min walk" },
      { slug: "sammy-s-woodfired-pizza", reason: "Casual pizza and salads, great for families", distance: "6-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/0540524a-1eae-4a49-a970-b31e671c65b4/powerhouse-park-0.jpg", alt: "Families enjoying a picnic on the grassy lawn at Powerhouse Park", caption: "A perfect spot for families to relax and enjoy the ocean breeze." },
      { src: "/__l5e/assets-v1/f24f43a6-a28c-4bee-916e-2943d9085be8/powerhouse-park-1.jpg", alt: "Kids playing in the sand at Del Mar Beach below Powerhouse Park", caption: "Easy access to the sandy beach for swimming and playing." },
      { src: "/__l5e/assets-v1/94171200-51b5-4767-a441-db10a041cb13/powerhouse-park-2.jpg", alt: "Golden sunset over the Pacific Ocean from Powerhouse Park", caption: "Breathtaking sunset views from the elevated park." }
    ],
    nearbyAttractions: [
      { to: "/beaches/del-mar-dog-beach", label: "Del Mar Dog Beach", hint: "Off-leash fun for your furry friends." },
      { to: "/guides/del-mar-village", label: "Del Mar Village", hint: "Charming shops, art galleries, and fine dining." },
      { to: "/surf", label: "Del Mar Surf", hint: "Check conditions for the beach break directly below." },
      { to: "/trails", label: "Coastal Trail", hint: "Walk north or south along the scenic bluff." }
    ],
  },

  "cardiff-reef": {
    coords: { lat: 33.0186, lng: -117.2814 },
    tideStationId: NORTH_COUNTY,
    activities: [
      { kind: "surf", rating: "great", note: "Classic longboard reef" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "great", note: "Grass strip above sand" },
      { kind: "bonfire", rating: "great", note: "State-run fire rings" },
      { kind: "photo", rating: "good" },
      { kind: "swim", rating: "good" },
      { kind: "dogs", rating: "avoid", note: "Not on state beach" },
    ],
    packing: {
      bring: ["Longboard or funshape", "Firewood + s'mores kit", "State parks day-use pass", "Wetsuit year-round"],
      rentNearby: ["Boards at Cardiff Surf Co (Chesterfield Dr)", "Coffee at Pipes Cafe"],
      skip: ["Dogs on the sand", "Glass, alcohol on state beach"],
    },
    photoGuide: {
      bestTime: "Sunset year-round — cliff shadows over the reef make golden lines.",
      bestSpots: ["The rock jetty at the north end", "Cardiff Kook statue on the PCH", "Reef at low tide"],
      tips: ["Long exposure over the reef at dusk = silky patterns.", "The Kook statue often has a seasonal outfit — a running local joke."],
    },
    seasonalCalendar: {
      summary: "Longboard heaven. Best surf Oct–Mar; warmest bonfire nights Jul–Sep.",
      rows: { crowd: [1, 1, 1, 2, 2, 2, 3, 3, 2, 2, 2, 1], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3], weather: SUN_NORTH },
      events: [
        { months: "Jul", label: "Switchfoot Bro-Am (surf + concert)" },
        { months: "Year-round", label: "Sunset bonfires (reserve state park fire rings)" },
      ],
    },
  
    bestFor: [
      { label: "Longboard Surfing", icon: "Waves" },
      { label: "Bonfires", icon: "Flame" },
      { label: "Sunset Views", icon: "Sun" }
    ],
    parkingSnapshot: {
      cost: "Free",
      bestLot: "San Elijo State Beach parking lot",
      backupLot: "Street parking on Coast Hwy 101",
      fillsBy: "8 AM on weekends, 10 AM weekdays",
      notes: "State beach lot provides easy access, fills very early."
    },
    dining: [
      { slug: "seaside-market", reason: "Home of the 'Cardiff Crack' tri-tip — perfect beach picnic", distance: "3-min drive" },
      { slug: "vg-donut-bakery", reason: "Cardiff's 5am donut ritual since 1969", distance: "4-min drive" },
      { slug: "las-olas", reason: "Beachside Mexican with ocean views and great ceviche", distance: "2-min walk" },
      { slug: "pacific-coast-grill", reason: "Bluff-top seafood and California cuisine with the best view", distance: "4-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/16a26ce2-346f-4b1d-981b-fa0ac99dd6be/cardiff-reef-0.jpg", alt: "Longboarders catching waves at Cardiff Reef with the Kook statue in the distance", caption: "Classic longboarding conditions at the famous Cardiff Reef." },
      { src: "/__l5e/assets-v1/5681a35f-b914-443d-8119-fb20bec14bbd/cardiff-reef-1.jpg", alt: "Group gathered around a bonfire on Cardiff State Beach at dusk", caption: "Enjoying a cozy bonfire as the sun sets over the ocean." },
      { src: "/__l5e/assets-v1/dc41028a-e5e5-4c3c-afb0-4cd05c52cbde/cardiff-reef-2.jpg", alt: "The 'Cardiff Kook' statue adorned with a humorous costume", caption: "The iconic Kook statue, often dressed in whimsical outfits." }
    ],
    nearbyAttractions: [
      { to: "/beaches/swamis", label: "Swami's Beach", hint: "Another famous surf spot just south of Cardiff." },
      { to: "/guides/cardiff-by-the-sea", label: "Cardiff-by-the-Sea", hint: "Quaint beach town with unique shops and eateries." },
      { to: "/surf", label: "Surf Forecast", hint: "Check wave conditions for Cardiff Reef and nearby breaks." },
      { to: "/trails", label: "Coastal Rail Trail", hint: "Bike or walk along the scenic coastal path." }
    ],
  },

  "blacks-beach": {
    coords: { lat: 32.8843, lng: -117.253 },
    tideStationId: LA_JOLLA,
    activities: [
      { kind: "surf", rating: "great", note: "Powerful A-frame — intermediate+" },
      { kind: "sunset", rating: "great", note: "Sandstone glows gold" },
      { kind: "photo", rating: "great" },
      { kind: "swim", rating: "fair", note: "No lifeguards, strong rips" },
      { kind: "dogs", rating: "avoid", note: "Not permitted" },
      { kind: "bonfire", rating: "avoid" },
    ],
    packing: {
      bring: ["Sturdy shoes for the trail down", "Water — no facilities", "Sun protection", "Wetsuit (3/2 year-round)"],
      rentNearby: ["Nothing on-site — Cheap Rentals in PB or Mitch's in La Jolla", "Snacks in La Jolla or Del Mar"],
      skip: ["Anything you can't carry back up the trail", "Glass", "Any expectation of lifeguards"],
    },
    photoGuide: {
      bestTime: "One hour before sunset — sandstone bluffs turn orange.",
      bestSpots: ["Beach base of Ho Chi Minh Trail", "Torrey Pines Gliderport above (paragliders + sunset)", "North end for reef + surf frames"],
      tips: ["Wide lens captures the 300-ft bluffs above.", "Paragliders overhead in afternoon add scale."],
    },
    seasonalCalendar: {
      summary: "Big surf and big cliffs. Fall and winter have the biggest swells; spring is quietest.",
      rows: { crowd: [1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 1, 1], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_SD },
      events: [{ months: "Nov–Feb", label: "Peak winter swell — one of the best beachbreaks on the coast" }],
    },
  
    bestFor: [
      { label: "Expert Surfing", icon: "Waves" },
      { label: "Paragliding", icon: "Car" },
      { label: "Secluded Beach", icon: "MapPin" }
    ],
    parkingSnapshot: {
      cost: "Free",
      bestLot: "Torrey Pines Gliderport parking lot",
      backupLot: "Street parking on La Jolla Farms Rd",
      fillsBy: "8 AM on weekends, 10 AM weekdays",
      notes: "Limited parking at the top of the cliffs, prepare for a hike."
    },
    dining: [
      { slug: "zel-s-del-mar", reason: "Casual pub food after the long climb back up the trail", distance: "9-min drive" },
      { slug: "board-brew", reason: "Quick sandwiches and a beer on the way home", distance: "9-min drive" },
      { slug: "the-marine-room", reason: "Upscale oceanfront dining in La Jolla, a short drive south", distance: "10-min drive" },
      { slug: "the-fish-market", reason: "Fresh seafood with bay views south of the cliffs", distance: "20-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/de8e5390-576f-4e9c-93b7-fdb4aba334ac/blacks-beach-0.jpg", alt: "Powerful A-frame wave breaking at Blacks Beach with cliffs in background", caption: "Surfers tackling the legendary and challenging waves of Blacks Beach." },
      { src: "/__l5e/assets-v1/887e7c85-61f6-4453-be43-89e6753f6f1e/blacks-beach-1.jpg", alt: "Paragliders soaring above the cliffs at Torrey Pines Gliderport", caption: "Colorful paragliders enjoying the thermals above the dramatic cliffs." },
      { src: "/__l5e/assets-v1/7585ae3e-3de3-48d4-90ad-ddb1f7e945f2/blacks-beach-2.jpg", alt: "Secluded stretch of Blacks Beach with towering cliffs", caption: "The pristine and less crowded sands beneath the imposing cliffs." }
    ],
    nearbyAttractions: [
      { to: "/guides/torrey-pines-state-natural-reserve", label: "Torrey Pines Reserve", hint: "Hiking trails with stunning coastal views and unique trees." },
      { to: "/beaches/la-jolla-shores", label: "La Jolla Shores", hint: "Family-friendly beach with calmer waves and kayak rentals." },
      { to: "/surf", label: "Surf Report", hint: "Check conditions for advanced surfers at this powerful break." },
      { to: "/trails", label: "Beach Access Trails", hint: "Steep trails lead down to this remote and beautiful beach." }
    ],
  },

  "swamis": {
    coords: { lat: 33.0342, lng: -117.2939 },
    tideStationId: NORTH_COUNTY,
    activities: [
      { kind: "surf", rating: "great", note: "Legendary right-hand reef" },
      { kind: "sunset", rating: "great" },
      { kind: "photo", rating: "great", note: "Self-Realization Fellowship domes" },
      { kind: "picnic", rating: "good", note: "Bluff-top grass" },
      { kind: "swim", rating: "fair", note: "Reef bottom, no lifeguards near reef" },
      { kind: "dogs", rating: "fair", note: "Leashed on bluff, not on state beach" },
    ],
    packing: {
      bring: ["Longboard or shortboard", "Wetsuit (3/2 year-round)", "Reef booties recommended", "Meditation mindset — this is a spiritual spot"],
      rentNearby: ["Boards at Encinitas Surfboards or Hansen's", "Coffee at Better Buzz Encinitas"],
      skip: ["Beginner lessons — go to Cardiff or San Onofre Bluffs", "Dropping in — localism is real"],
    },
    photoGuide: {
      bestTime: "Sunset — the SRF Fellowship golden domes glow above the reef.",
      bestSpots: ["Bluff overlook by the SRF gardens", "Stairway down to the reef at low tide", "PCH from the SRF gate for the classic wide"],
      tips: ["Telephoto from the bluff captures surfers with the domes above.", "Winter storms produce dramatic reef spray."],
    },
    seasonalCalendar: {
      summary: "West-coast surf icon. Best waves Oct–Mar; the SRF gardens are open year-round for meditation views.",
      rows: { crowd: [2, 2, 2, 2, 2, 2, 3, 3, 2, 2, 2, 2], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3], weather: SUN_NORTH },
      events: [{ months: "Nov–Mar", label: "Prime NW swell season — the reef fires" }],
    },
  
    bestFor: [
      { label: "Surfing", icon: "Waves" },
      { label: "Meditation", icon: "Heart" },
      { label: "Sunset Views", icon: "Sun" }
    ],
    parkingSnapshot: {
      cost: "Free",
      bestLot: "Lower parking lot off Highway 101",
      backupLot: "Street parking on South Coast Hwy 101",
      fillsBy: "8 AM on weekends",
      notes: "Limited spots, arrive early for best chance."
    },
    dining: [
      { slug: "swami-s-cafe", reason: "The dawn-patrol canteen directly across the 101", distance: "1-min walk" },
      { slug: "juanita-s-taco-shop", reason: "Open-late rolled tacos and carne asada burritos", distance: "4-min drive" },
      { slug: "vg-donut-bakery", reason: "Buttermilk bars before the sun clears the bluff", distance: "5-min drive" },
      { slug: "herb-sea", reason: "Wood-grilled seafood and cocktails for dinner in Encinitas", distance: "5-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/7fdcafb5-87e4-420f-87ba-accf947a0e71/swamis-0.jpg", alt: "Surfer riding a wave at Swamis Beach during sunset.", caption: "A lone surfer catches a perfect right-hand wave as the sun dips below the horizon at Swamis." },
      { src: "/__l5e/assets-v1/84c46ad7-6e83-4991-bd3a-3be98433f54c/swamis-1.jpg", alt: "Self-Realization Fellowship domes overlooking Swamis Beach.", caption: "The iconic golden domes of the Self-Realization Fellowship against the blue sky, overlooking the Pacific." },
      { src: "/__l5e/assets-v1/0ada46a8-03b4-419a-ab7c-653d10fda9e6/swamis-2.jpg", alt: "People meditating in the Self-Realization Fellowship gardens.", caption: "Tranquil meditation gardens offering serene views and a peaceful atmosphere near Swamis." }
    ],
    nearbyAttractions: [
      { to: "/guides/encinitas-surf-spots", label: "Encinitas Surf Spots", hint: "Explore other renowned surf breaks in Encinitas." },
      { to: "/guides/best-surf-spots-by-skill-level", label: "Swamis Bluff Trail", hint: "Scenic walk along the bluffs with ocean views." },
      { to: "/dining", label: "Encinitas Dining Guide", hint: "Discover more culinary delights in the charming city." },
      { to: "/guides/self-realization-fellowship", label: "Self-Realization Fellowship", hint: "Visit the peaceful gardens and learn about the fellowship." }
    ],
  },

  "san-onofre": {
    coords: { lat: 33.3728, lng: -117.5658 },
    tideStationId: NORTH_COUNTY,
    activities: [
      { kind: "surf", rating: "great", note: "Old Man's — America's mellowest wave" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "great", note: "Bluffs + campfire pits" },
      { kind: "bonfire", rating: "great", note: "State park pits" },
      { kind: "dogs", rating: "avoid", note: "Not on beach" },
      { kind: "swim", rating: "good" },
    ],
    packing: {
      bring: ["Longboard (mandatory culture)", "Beach chair + umbrella", "State parks day-use pass", "Firewood for pit"],
      rentNearby: ["Boards at Trestles Surf Shop (San Clemente)", "Groceries in San Clemente — nothing on-site"],
      skip: ["Aggressive shortboarding — it's a longboard culture", "Glass"],
    },
    photoGuide: {
      bestTime: "Late afternoon — sun paints the bluffs, and surfers stream past all day.",
      bestSpots: ["Old Man's takeoff zone from the sand", "Bluff Trail above the beach", "Dogpatch to the north for empty-sand frames"],
      tips: ["Wide-angle from the sand captures the surf-family scene.", "Long exposure of glassy lineup at dawn is legendary."],
    },
    seasonalCalendar: {
      summary: "The mellowest wave in California. Consistent year-round; September is peak for warm-water longboarding.",
      rows: { crowd: [1, 1, 1, 2, 2, 2, 3, 3, 3, 2, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_NORTH },
      events: [{ months: "Sep", label: "SanO Longboard Contest — free to watch" }],
    },
  
    bestFor: [
      { label: "Longboarding", icon: "Waves" },
      { label: "Camping", icon: "Flame" },
      { label: "Beach Days", icon: "Sun" }
    ],
    parkingSnapshot: {
      cost: "$15-$20 per vehicle (State Park fee)",
      bestLot: "Old Man's parking lot",
      backupLot: "Trail 6 parking lot",
      fillsBy: "9 AM on weekends",
      notes: "State Park entrance fee required. Lots fill quickly."
    },
    dining: [
      { slug: "pedro-s-tacos", reason: "The classic surfer stop — white sauce and fish tacos", distance: "10-min drive" },
      { slug: "seaside-market", reason: "Stock the cooler with Cardiff Crack on the drive up", distance: "30-min drive" },
      { slug: "fish-101", reason: "Fresh poke bowls and fish tacos on the way home", distance: "25-min drive" },
      { slug: "beach-break-cafe", reason: "Huge post-session breakfast plates in Oceanside", distance: "20-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/af6c0605-3c18-425e-876c-f25a512bd20c/san-onofre-0.jpg", alt: "Longboarders riding gentle waves at San Onofre State Beach.", caption: "Classic longboarding waves at Old Man's, a favorite spot for all ages." },
      { src: "/__l5e/assets-v1/a7e94545-e0b0-4540-94d4-67139cc6c09d/san-onofre-1.jpg", alt: "Campfire pits on the bluffs overlooking San Onofre beach.", caption: "Cozy campfire pits on the bluffs, perfect for evening gatherings and stargazing." },
      { src: "/__l5e/assets-v1/8be0c406-9c02-4368-9c2c-43eebb02f9e3/san-onofre-2.jpg", alt: "Scenic bluffs overlooking the Pacific Ocean at San Onofre.", caption: "Dramatic bluffs offer stunning panoramic views of the coastline and ocean." }
    ],
    nearbyAttractions: [
      { to: "/guides/camp-pendleton-history", label: "Camp Pendleton History", hint: "Learn about the military base adjacent to the beach." },
      { to: "/surf", label: "San Onofre Surf Guide", hint: "Detailed information on the various surf breaks at San O." },
      { to: "/guides/best-surf-spots-by-skill-level", label: "San Onofre Bluff Trails", hint: "Explore walking trails along the scenic coastal bluffs." },
      { to: "/tides", label: "Current Tide Charts", hint: "Check the tides for optimal surfing and beach activities." }
    ],
  },

  "oceanside-pier": {
    coords: { lat: 33.1936, lng: -117.3856 },
    tideStationId: NORTH_COUNTY,
    activities: [
      { kind: "surf", rating: "great", note: "Consistent beachbreak both sides of pier" },
      { kind: "fish", rating: "great", note: "Free fishing on the pier" },
      { kind: "swim", rating: "good" },
      { kind: "sunset", rating: "great" },
      { kind: "bike", rating: "great", note: "Strand bike path" },
      { kind: "picnic", rating: "good" },
      { kind: "dogs", rating: "avoid", note: "Not on beach" },
    ],
    packing: {
      bring: ["Fishing gear (no license needed on pier)", "Beach cruiser", "Sunscreen", "Cash for Ruby's at pier end (currently closed post-fire — check status)"],
      rentNearby: ["Boards at Oceanside Surf Shop", "Bikes at Wheel Fun Rentals on The Strand"],
      skip: ["Glass, alcohol", "Off-leash dogs"],
    },
    photoGuide: {
      bestTime: "Sunset walking down the pier — pilings frame the sun.",
      bestSpots: ["Pier end at sunset", "Sand looking up at the pier structure", "The Strand at blue hour"],
      tips: ["April 2024 fire damaged the pier end — check current access.", "Long exposure below the pier for silky water + pilings."],
    },
    seasonalCalendar: {
      summary: "Working-town beach vibe. Best fishing spring/summer; surf peaks with fall swells.",
      rows: { crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3], weather: SUN_NORTH },
      events: [
        { months: "Aug", label: "Supergirl Pro surf contest" },
        { months: "Jul 4", label: "Independence Day pier fireworks (check schedule)" },
      ],
    },
  
    bestFor: [
      { label: "Pier Fishing", icon: "Fish" },
      { label: "Cycling", icon: "Bike" },
      { label: "Consistent Surf", icon: "Waves" }
    ],
    parkingSnapshot: {
      cost: "Metered street parking / Paid lots",
      bestLot: "Oceanside Pier Parking Structure",
      backupLot: "Street parking along Pacific Street",
      fillsBy: "10 AM daily",
      notes: "Paid parking is common, especially near the pier."
    },
    dining: [
      { slug: "petite-madeline-bakery", reason: "Croissants and full breakfast two blocks from the pier", distance: "4-min walk" },
      { slug: "beach-break-cafe", reason: "Classic surf-town breakfast, wetsuits still dripping", distance: "6-min drive" },
      { slug: "that-boy-good-bbq", reason: "Slow-smoked brisket and ribs on the Coast Highway", distance: "5-min drive" },
      { slug: "hello-betty-fish-house", reason: "Rooftop patio, fish tacos and cocktails steps from the pier", distance: "3-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/8f017208-dd32-4ca2-a2b5-782f96f83a38/oceanside-pier-0.jpg", alt: "Oceanside Pier stretching into the Pacific Ocean at sunset.", caption: "The iconic Oceanside Pier, a hub of activity and stunning sunset views." },
      { src: "/__l5e/assets-v1/14d77042-8d34-40c6-b842-64e660bd68b1/oceanside-pier-1.jpg", alt: "People fishing off Oceanside Pier with fishing rods.", caption: "Anglers enjoying free pier fishing, hoping for a fresh catch." },
      { src: "/__l5e/assets-v1/d0639dae-e129-45be-8e25-c04f11041e7f/oceanside-pier-2.jpg", alt: "Cyclists riding along The Strand bike path in Oceanside.", caption: "The scenic Strand bike path, perfect for a leisurely ride along the coast." }
    ],
    nearbyAttractions: [
      { to: "/guides/oceanside-harbor", label: "Oceanside Harbor", hint: "Explore shops, restaurants, and boat tours at the harbor." },
      { to: "/surf", label: "Oceanside Surf Report", hint: "Get the latest surf conditions and forecasts for the area." },
      { to: "/guides/mission-beach-boardwalk", label: "The Strand Bike Path", hint: "Enjoy a flat, paved path perfect for cycling and walking." },
      { to: "/dining", label: "Oceanside Dining Guide", hint: "Discover diverse culinary options in downtown Oceanside." }
    ],
  },

  "tamarack": {
    coords: { lat: 33.1372, lng: -117.3506 },
    tideStationId: NORTH_COUNTY,
    activities: [
      { kind: "surf", rating: "great", note: "Consistent Carlsbad beachbreak" },
      { kind: "swim", rating: "good" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good", note: "Bluff-top grass" },
      { kind: "photo", rating: "good" },
      { kind: "dogs", rating: "avoid", note: "Not permitted" },
    ],
    packing: {
      bring: ["Shortboard or funshape", "Wetsuit", "State parks pass", "Sunscreen"],
      rentNearby: ["Boards at Carlsbad Surfboards", "Snacks in Carlsbad Village (5 min)"],
      skip: ["Off-leash dogs", "Glass"],
    },
    photoGuide: {
      bestTime: "Sunset from the bluff-top parking area — surf spray backlit.",
      bestSpots: ["Tamarack bluff stairs", "North end at low tide", "Carlsbad Sea Wall at sunset"],
      tips: ["Bring a telephoto to capture surfers against the setting sun.", "Winter storms turn the shore-break dramatic."],
    },
    seasonalCalendar: {
      summary: "Carlsbad's most consistent surf beach. Fall and winter deliver the best waves; summer is family-friendly.",
      rows: { crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3], weather: SUN_NORTH },
    },
  
    bestFor: [
      { label: "Beachcombing", icon: "Sun" },
      { label: "Picnics", icon: "Users" },
      { label: "Surfing", icon: "Waves" }
    ],
    parkingSnapshot: {
      cost: "Metered street parking / State Beach lot",
      bestLot: "Tamarack State Beach parking lot",
      backupLot: "Street parking on Carlsbad Blvd",
      fillsBy: "9 AM on weekends",
      notes: "State Beach lot has limited capacity, street parking can be competitive."
    },
    dining: [
      { slug: "senor-grubby-s", reason: "Oversized burritos and cheap pitchers after a session", distance: "5-min drive" },
      { slug: "pollos-maria", reason: "Mesquite-charred chicken to take back to the sand", distance: "4-min drive" },
      { slug: "pizza-port", reason: "Award-winning craft beer and big pies in the Village", distance: "6-min drive" },
      { slug: "campfire", reason: "Wood-fired dinner and cocktails in Carlsbad Village", distance: "6-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/bec5cffa-f2d7-4036-8c7b-56309e4ae60d/tamarack-0.jpg", alt: "People enjoying the sandy beach at Tamarack State Beach.", caption: "Wide sandy beach at Tamarack, perfect for sunbathing and relaxation." },
      { src: "/__l5e/assets-v1/9abcb189-65aa-4d0b-ae3f-a18b6f2e0e68/tamarack-1.jpg", alt: "Grassy bluff overlooking Tamarack State Beach with ocean views.", caption: "Expansive grassy bluffs provide excellent picnic spots with ocean vistas." },
      { src: "/__l5e/assets-v1/cc9dfe43-0445-415d-86f6-09b5281c7819/tamarack-2.jpg", alt: "Surfers catching waves at Tamarack beach break.", caption: "Consistent beach break offers fun waves for surfers of various skill levels." }
    ],
    nearbyAttractions: [
      { to: "/guides/carlsbad-village", label: "Carlsbad Village", hint: "Explore charming shops, restaurants, and breweries nearby." },
      { to: "/guides/best-beaches-in-san-diego", label: "Carlsbad Coastal Rail Trail", hint: "Walk or bike along this scenic trail near the coast." },
      { to: "/dining", label: "Carlsbad Dining Options", hint: "Discover a variety of dining experiences in Carlsbad." },
      { to: "/guides/legoland-california", label: "LEGOLAND California", hint: "Family-friendly theme park just a short drive away." }
    ],
  },

  "moonlight-beach": {
    coords: { lat: 33.0447, lng: -117.2969 },
    tideStationId: NORTH_COUNTY,
    activities: [
      { kind: "swim", rating: "great", note: "Family favorite" },
      { kind: "surf", rating: "good", note: "Learner-friendly beachbreak" },
      { kind: "volleyball", rating: "great", note: "Permanent nets" },
      { kind: "picnic", rating: "great", note: "Grass + BBQs + playground" },
      { kind: "sunset", rating: "great" },
      { kind: "bonfire", rating: "great", note: "Fire rings on the sand" },
      { kind: "dogs", rating: "avoid", note: "Not on beach" },
    ],
    packing: {
      bring: ["Beach chairs", "Cooler (no glass)", "Firewood", "Volleyball or paddle set"],
      rentNearby: ["Boards at Hansen's or Encinitas Surfboards", "Coffee at Better Buzz Encinitas"],
      skip: ["Off-leash dogs", "Glass containers"],
    },
    photoGuide: {
      bestTime: "Sunset year-round — south-facing cove keeps the sun visible longer.",
      bestSpots: ["Overlook above the beach at B St stairs", "South-end rocks at low tide", "Beach volleyball nets for action shots"],
      tips: ["Blue hour is 20 min after sunset — kids' silhouettes on the sand.", "Winter storms produce dramatic cove waves."],
    },
    seasonalCalendar: {
      summary: "Encinitas' family beach. Peak summer scene Jul–Aug; September is warmer and quieter.",
      rows: { crowd: [1, 1, 1, 2, 2, 3, 3, 3, 2, 2, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2], weather: SUN_NORTH },
      events: [{ months: "Aug", label: "Peak Encinitas summer scene" }],
    },
  
    bestFor: [
      { label: "Families", icon: "Users" },
      { label: "Beach Volleyball", icon: "Sun" },
      { label: "Bonfires", icon: "Flame" }
    ],
    parkingSnapshot: {
      cost: "Free parking lot / Metered street parking",
      bestLot: "Moonlight Beach parking lot",
      backupLot: "Street parking on B Street and C Street",
      fillsBy: "8 AM daily",
      notes: "Extremely popular, parking fills very early, especially on weekends."
    },
    dining: [
      { slug: "swami-s-cafe", reason: "Breakfast plates and smoothies four blocks up the 101", distance: "6-min walk" },
      { slug: "juanita-s-taco-shop", reason: "Late-night tacos after a bonfire on the sand", distance: "5-min walk" },
      { slug: "fish-101", reason: "No-frills fish shack the locals swear by", distance: "5-min drive" },
      { slug: "herb-sea", reason: "Buzzy dining room a block off the 101 for date night", distance: "8-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/94fa6d33-fdfe-4fa2-9e05-014a363c0199/moonlight-beach-0.jpg", alt: "Children playing on the playground at Moonlight Beach.", caption: "A vibrant playground provides endless fun for kids right on the sand." },
      { src: "/__l5e/assets-v1/e3b1d334-3fc4-40f6-94cd-88e54c6eb464/moonlight-beach-1.jpg", alt: "People playing volleyball on the sand courts at Moonlight Beach.", caption: "Active beachgoers enjoying a game of volleyball on the well-maintained courts." },
      { src: "/__l5e/assets-v1/1b5897e6-e051-4291-b1b7-29cc4197e44f/moonlight-beach-2.jpg", alt: "Families gathered around a bonfire pit at Moonlight Beach.", caption: "Families and friends gather around fire rings for evening bonfires and s'mores." }
    ],
    nearbyAttractions: [
      { to: "/guides/encinitas-downtown", label: "Downtown Encinitas", hint: "Explore unique boutiques, cafes, and art galleries." },
      { to: "/dining", label: "Encinitas Family Restaurants", hint: "Find kid-friendly dining options in the area." },
      { to: "/guides/san-dieguito-lagoon", label: "San Dieguito Lagoon", hint: "Nature preserve offering birdwatching and walking trails." },
      { to: "/tides", label: "Moonlight Beach Tides", hint: "Check the tide schedule for optimal beach activities." }
    ],
  },

  "fletcher-cove": {
    coords: { lat: 32.9944, lng: -117.2725 },
    tideStationId: LA_JOLLA,
    activities: [
      { kind: "swim", rating: "good" },
      { kind: "surf", rating: "good", note: "Solana Beach beachbreak" },
      { kind: "picnic", rating: "great", note: "Bluff-top grass + park" },
      { kind: "sunset", rating: "great" },
      { kind: "photo", rating: "good" },
      { kind: "dogs", rating: "fair", note: "Leashed on bluff, not on sand" },
      { kind: "bonfire", rating: "avoid" },
    ],
    packing: {
      bring: ["Picnic blanket", "Sunscreen", "Layers for evening", "Kids' beach toys"],
      rentNearby: ["Boards at Solana Beach Surf Co", "Snacks along Highway 101"],
      skip: ["Off-leash dogs on beach", "Glass"],
    },
    photoGuide: {
      bestTime: "Sunset from the ramp down to the cove.",
      bestSpots: ["Top of Fletcher Cove ramp", "Bluff trail south toward Del Mar", "Sand at low tide near the point"],
      tips: ["The ramp itself makes a nice leading-line composition.", "Winter king tides push waves against the bluff base."],
    },
    seasonalCalendar: {
      summary: "The quiet, family-friendly cove of North County. Peak Jul–Aug; September is prime.",
      rows: { crowd: [1, 1, 1, 2, 2, 2, 3, 3, 2, 1, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_NORTH },
    },
  
    bestFor: [
      { label: "Families", icon: "Users" },
      { label: "Picnics", icon: "Heart" },
      { label: "Easy Access", icon: "Car" }
    ],
    parkingSnapshot: {
      cost: "Free",
      bestLot: "Fletcher Cove Parking Lot",
      backupLot: "Street parking on Pacific Ave",
      fillsBy: "10 AM on weekends",
      notes: "Limited spots, especially during peak season."
    },
    dining: [
      { slug: "fidel-s-little-mexico", reason: "Hacienda courtyards and margaritas, a 1960 institution", distance: "4-min drive" },
      { slug: "naked-cafe", reason: "Sun-drenched patio for veggie scrambles and bowls", distance: "5-min walk" },
      { slug: "pillbox-tavern", reason: "Cedros District gastropub with craft beer and a sunny patio", distance: "4-min walk" },
      { slug: "pizza-port", reason: "Casual pizza and beer a few blocks inland", distance: "5-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/7940ec0a-7ced-4b95-abcd-b0b9679d98f7/fletcher-cove-0.jpg", alt: "Children playing in the gentle waves at Fletcher Cove", caption: "Families enjoy the calm waters and sandy shores of Fletcher Cove." },
      { src: "/__l5e/assets-v1/ffa430e9-9ca8-4e56-ac77-37b3ba21e645/fletcher-cove-1.jpg", alt: "Bluff-top park overlooking Fletcher Cove at sunset", caption: "Panoramic views from the bluff-top park, perfect for sunset watching." },
      { src: "/__l5e/assets-v1/657da2a9-607c-409e-a3cf-01cd45fe2a19/fletcher-cove-2.jpg", alt: "Ramp leading down to Fletcher Cove beach", caption: "Convenient ramp access makes getting to the beach easy for everyone." }
    ],
    nearbyAttractions: [
      { to: "/guides/solana-beach", label: "Solana Beach Guide", hint: "Explore more of Solana Beach's charm and attractions." },
      { to: "/guides/best-beaches-for-sunset", label: "Coast to Crest Trail", hint: "Scenic hiking and biking trails nearby for outdoor enthusiasts." },
      { to: "/dining", label: "Solana Beach Dining", hint: "Discover diverse culinary options around Fletcher Cove." },
      { to: "/surf", label: "Del Mar Surfing", hint: "Catch waves at nearby Del Mar beaches, just south." }
    ],
  },

  "childrens-pool": {
    coords: { lat: 32.8508, lng: -117.2782 },
    tideStationId: LA_JOLLA,
    activities: [
      { kind: "photo", rating: "great", note: "Harbor seals + pups Dec–May" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good", note: "Grass above the seawall" },
      { kind: "swim", rating: "avoid", note: "Closed to swimmers during seal pupping" },
      { kind: "dogs", rating: "avoid", note: "Not permitted (marine mammal protection)" },
      { kind: "snorkel", rating: "avoid" },
    ],
    packing: {
      bring: ["Camera + telephoto lens (100–300mm)", "Sunscreen", "Binoculars"],
      rentNearby: ["Snorkel gear at Everyday California (if going to the Cove instead)", "Coffee in La Jolla village"],
      skip: ["Harassing seals — federal fines up to $10k", "Dogs (prohibited)", "Any thought of swimming during pupping season"],
    },
    photoGuide: {
      bestTime: "Late afternoon light hits the seals on the sand — soft warm tones.",
      bestSpots: ["Top of the seawall (safe distance from seals)", "Bluff overlook to the north", "Coast Blvd looking down"],
      tips: ["Keep 50 ft from the seals — use zoom, not proximity.", "Dec–Feb is pupping season — the best photos and the strictest rules."],
    },
    seasonalCalendar: {
      summary: "A seal sanctuary as much as a beach. Pupping season Dec–May is the highlight.",
      rows: { crowd: [1, 1, 1, 1, 1, 2, 3, 3, 2, 1, 1, 1], water: WATER_TEMP_SD, wildlife: [3, 3, 3, 3, 3, 2, 1, 1, 1, 2, 3, 3], weather: SUN_SD },
      events: [
        { months: "Dec 15–May 15", label: "Harbor seal pupping season — beach closed to public" },
        { months: "Year-round", label: "Seals visible from the seawall daily" },
      ],
    },
  
    bestFor: [
      { label: "Wildlife Viewing", icon: "Fish" },
      { label: "Photography", icon: "Camera" },
      { label: "Scenic Walks", icon: "Heart" }
    ],
    parkingSnapshot: {
      cost: "Metered Street Parking",
      bestLot: "Coast Blvd. Street Parking",
      backupLot: "Valet at La Jolla Cove restaurants",
      fillsBy: "9 AM daily",
      notes: "Extremely limited parking, especially on weekends. Arrive early."
    },
    dining: [
      { slug: "cody-s-la-jolla", reason: "Breakfast patio a block from the seal viewing wall", distance: "2-min walk" },
      { slug: "the-taco-stand", reason: "Quick, excellent tacos when the village prices bite", distance: "7-min walk" },
      { slug: "puesto-la-jolla", reason: "Street-food tacos and cocktails in the village", distance: "8-min walk" },
      { slug: "george-s-at-the-cove", reason: "Upscale dining with stunning ocean views, very close by", distance: "5-min walk" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/b99f3911-91e5-4075-959b-3e0adb8210b7/childrens-pool-0.jpg", alt: "Harbor seals resting on the sand at Children's Pool", caption: "Harbor seals often bask on the sandy beach, especially during pupping season." },
      { src: "/__l5e/assets-v1/1410cee0-9fc2-4b02-ae0d-f64a72c5055f/childrens-pool-1.jpg", alt: "Seawall at Children's Pool with waves crashing", caption: "The historic seawall provides a safe viewing platform for visitors." },
      { src: "/__l5e/assets-v1/d30be989-b080-410c-a035-7e46fa5f9e61/childrens-pool-2.jpg", alt: "Panoramic view of La Jolla coastline from Children's Pool", caption: "Stunning coastal views stretch along the La Jolla shoreline." }
    ],
    nearbyAttractions: [
      { to: "/guides/la-jolla", label: "La Jolla Guide", hint: "Explore La Jolla's upscale shops, galleries, and dining." },
      { to: "/beaches/la-jolla-cove", label: "La Jolla Cove", hint: "Snorkeling and swimming at the beautiful adjacent La Jolla Cove." },
      { to: "/guides/la-jolla-cove-sea-lions", label: "La Jolla Coast Walk", hint: "Scenic coastal trail offering breathtaking ocean vistas." },
      { to: "/dining", label: "La Jolla Dining", hint: "Indulge in world-class cuisine in charming La Jolla." }
    ],
  },

  "silver-strand": {
    coords: { lat: 32.6303, lng: -117.1381 },
    tideStationId: SOUTH_BAY,
    activities: [
      { kind: "swim", rating: "great", note: "Gentle bay-side + ocean-side" },
      { kind: "bike", rating: "great", note: "Bayshore Bikeway" },
      { kind: "picnic", rating: "great", note: "State beach lots + BBQs" },
      { kind: "bonfire", rating: "great", note: "State park fire rings" },
      { kind: "sunset", rating: "good" },
      { kind: "sup", rating: "great", note: "Bay side is glassy" },
      { kind: "dogs", rating: "avoid", note: "Not on beach" },
    ],
    packing: {
      bring: ["State parks pass", "Beach chairs & umbrella", "Firewood", "Bikes for the Bayshore Bikeway"],
      rentNearby: ["Bikes on Coronado (10 min north)", "SUPs at Silver Strand SUP"],
      skip: ["Dogs", "Glass"],
    },
    photoGuide: {
      bestTime: "Sunset over the Pacific with the Silver Strand ridge behind you.",
      bestSpots: ["Ocean-side sand at any lot", "Bay-side lagoon for calm reflections", "Coronado Cays overlook to the north"],
      tips: ["Bay side is perfect for calm-water long exposures at dawn.", "Bring a wide lens — the beach is very wide."],
    },
    seasonalCalendar: {
      summary: "Uncrowded family beach with fire rings. Peak Jul–Aug; September has warm water and empty sand.",
      rows: { crowd: [1, 1, 1, 1, 2, 2, 3, 3, 2, 1, 1, 1], water: WATER_TEMP_SD, surf: [2, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2], weather: SUN_SD },
    },
  
    bestFor: [
      { label: "Camping", icon: "Flame" },
      { label: "Biking", icon: "Bike" },
      { label: "Beach Fires", icon: "Flame" }
    ],
    parkingSnapshot: {
      cost: "Day Use Fee ($15-20)",
      bestLot: "State Park Main Lot",
      backupLot: "Along Highway 75 (limited)",
      fillsBy: "11 AM on weekends",
      notes: "Large lot, but fills quickly on warm days. RV camping available."
    },
    dining: [
      { slug: "coronado-brewing-company", reason: "House beers and fish tacos on the way back into town", distance: "12-min drive" },
      { slug: "clayton-s-coffee-shop", reason: "Cheap, old-school breakfast before a beach day", distance: "12-min drive" },
      { slug: "sea180-coastal-tavern", reason: "Ocean-view tavern and brunch at the south end of the Strand", distance: "10-min drive" },
      { slug: "1500-ocean", reason: "Fine dining at Hotel del Coronado, a short drive north", distance: "12-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/dcb41599-ebc7-4250-86cc-677f2c776d18/silver-strand-0.jpg", alt: "Fire rings on Silver Strand State Beach at sunset", caption: "Enjoy a cozy bonfire with friends and family as the sun sets." },
      { src: "/__l5e/assets-v1/049ecfc7-3f53-47e4-8b45-f313312530ec/silver-strand-1.jpg", alt: "Cyclists on the Bayshore Bikeway adjacent to Silver Strand", caption: "The scenic Bayshore Bikeway offers miles of flat, coastal riding." },
      { src: "/__l5e/assets-v1/1d079a07-9a86-4ae5-a326-15a5fd1fb94f/silver-strand-2.jpg", alt: "Calm bay waters on the eastern side of Silver Strand", caption: "The bay side offers tranquil waters perfect for kayaking and paddleboarding." }
    ],
    nearbyAttractions: [
      { to: "/guides/coronado", label: "Coronado Guide", hint: "Discover the charming island city of Coronado." },
      { to: "/beaches/coronado-beach", label: "Coronado Beach", hint: "Iconic beach with the Hotel del Coronado, just north." },
      { to: "/guides/best-beaches-in-san-diego", label: "Bayshore Bikeway", hint: "Miles of scenic cycling along San Diego Bay." },
      { to: "/dining", label: "Coronado Dining", hint: "Explore diverse dining options in nearby Coronado." }
    ],
  },

  "imperial-beach": {
    coords: { lat: 32.5839, lng: -117.1339 },
    tideStationId: SOUTH_BAY,
    activities: [
      { kind: "swim", rating: "fair", note: "Check TJ river water-quality advisories" },
      { kind: "surf", rating: "good", note: "Consistent beachbreak" },
      { kind: "fish", rating: "great", note: "Free on the IB Pier" },
      { kind: "sunset", rating: "great" },
      { kind: "picnic", rating: "good" },
      { kind: "bike", rating: "great", note: "Bayshore Bikeway terminus" },
      { kind: "dogs", rating: "avoid", note: "Not on beach" },
    ],
    packing: {
      bring: ["Fishing gear", "Sunscreen", "Water — limited concessions", "Sand toys"],
      rentNearby: ["Boards at IB Surf Lounge", "Bikes on Seacoast Dr"],
      skip: ["Swimming during posted TJ River advisories", "Off-leash dogs", "Glass"],
    },
    photoGuide: {
      bestTime: "Sunset over the Pacific from the IB Pier end.",
      bestSpots: ["End of the IB Pier", "South end of Seacoast Dr", "Sand-castle competition site in August"],
      tips: ["Sunday walks on the pier catch fishing families at work.", "Check SDBeachInfo.com for water-quality advisories before shooting waders."],
    },
    seasonalCalendar: {
      summary: "SD's southernmost beach. Best conditions summer/fall; winter rain often triggers TJ River water advisories.",
      rows: { crowd: [1, 1, 1, 1, 2, 2, 3, 2, 2, 1, 1, 1], water: WATER_TEMP_SD, surf: [3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3], weather: SUN_SD },
      events: [{ months: "Aug", label: "US Open Sandcastle Competition" }],
    },
  
    bestFor: [
      { label: "Fishing", icon: "Fish" },
      { label: "Surfing", icon: "Waves" },
      { label: "Local Vibe", icon: "Heart" }
    ],
    parkingSnapshot: {
      cost: "Free Street Parking",
      bestLot: "Pier Plaza Parking Lot",
      backupLot: "Residential street parking (further walk)",
      fillsBy: "10 AM on weekends",
      notes: "Can be challenging to find parking during events like the Sandcastle Competition."
    },
    dining: [
      { slug: "the-tin-fish-imperial-beach", reason: "Beer-battered fish and shrimp tacos literally on the pier", distance: "1-min walk" },
      { slug: "sea180-coastal-tavern", reason: "Ocean-view tavern with the best sunset table in South Bay", distance: "3-min walk" },
      { slug: "tj-oyster-bar", reason: "Baja-style oysters and micheladas, a South Bay legend", distance: "12-min drive" },
      { slug: "coronado-brewing-company", reason: "Brewpub burgers and IPAs up the Strand in Coronado", distance: "15-min drive" },
    ],
    photoGallery: [
      { src: "/__l5e/assets-v1/52a2124c-c54a-4763-94ee-b193b524081c/imperial-beach-0.jpg", alt: "Imperial Beach Pier at sunset with fishermen", caption: "The iconic Imperial Beach Pier is a popular spot for fishing and sunset views." },
      { src: "/__l5e/assets-v1/289f1f17-ea6a-4b56-a396-7785b39b9b87/imperial-beach-1.jpg", alt: "Surfers riding waves at Imperial Beach", caption: "Imperial Beach offers consistent waves, attracting surfers of all levels." },
      { src: "/__l5e/assets-v1/085fe23e-dfd9-4370-9473-10f4b899c6ae/imperial-beach-2.jpg", alt: "Sandcastle sculpture during the Imperial Beach competition", caption: "Witness incredible sandcastle artistry at the annual competition." }
    ],
    nearbyAttractions: [
      { to: "/guides/imperial-beach", label: "Imperial Beach Guide", hint: "Discover more about San Diego's southernmost beach town." },
      { to: "/guides/best-beaches-in-san-diego", label: "Tijuana River Valley", hint: "Explore the diverse ecosystem of the Tijuana River Valley." },
      { to: "/surf", label: "Imperial Beach Surfing", hint: "Catch waves at this popular surf spot for all levels." },
      { to: "/dining", label: "Imperial Beach Dining", hint: "Enjoy casual and authentic dining experiences in IB." }
    ],
  },
};
